import './index.css';
import logo from './assets/rsaa_logo_2.webp';
import { translations } from './translations';


import { BrowserRouter as Router, Link, Route, Routes, useLocation, useNavigationType } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Anchor,
  Calculator,
  CalendarDays,
  ExternalLink,
  Fish,
  FileText,
  MapPin,
  MessageCircle,
  Phone,
  ShieldCheck,
  ShieldAlert,
  Trophy,
  Users,
  Mail,
  ChevronLeft,
  ChevronRight,
  Scale,
  Leaf,
  PartyPopper,
  Sparkles,
  Newspaper,
  Images,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";

import FacebookNews from "./FacebookNews";  // embedded Facebook feed component
import FacebookGallery from "./FacebookGallery";
import FishingConditionsTeaser from "./FishingConditionsTeaser";
import ContentPage from "./ContentPage";
import PageMeta from "./PageMeta";
import MinutesPage from "./MinutesPage";
import AgendaPage from "./AgendaPage";
import UpcomingMeetingPage from "./UpcomingMeetingPage";
import TreasurerPage from "./TreasurerPage";
import MembersPage from "./MembersPage";
import RecordsPage from "./RecordsPage";
import RSAARecordsPage from "./RSAARecordsPage";
import JuniorRecordsPage from "./JuniorRecordsPage";
import JuniorCatchReleasePage from "./JuniorCatchReleasePage";
import CFSASpecimenPage from "./CFSASpecimenPage";
import SponsorsPage from "./SponsorsPage";
import ScalesPolicyPage from "./ScalesPolicyPage";
import SeaAnglingStrategyPage from "./SeaAnglingStrategyPage";
import WeighMeasurePage from "./WeighMeasurePage";
import MerchandisePage from "./MerchandisePage";
import CompetitionCalculatorPage from "./CompetitionCalculatorPage";
import AdminPage from "./AdminPage";
import LatestCatches from "./LatestCatches";
import FishOfMonthTeaser from "./FishOfMonthTeaser";
import RayBassHuntTeaser from "./RayBassHuntTeaser";
import Notifications from "./Notifications";
import SiteSearch from "./SiteSearch";
import AwardsPage from "./AwardsPage";
import RSAASpeciesHuntPage from "./RSAASpeciesHuntPage";
import RayBassHuntPage from "./RayBassHuntPage";
import BookmarkPage from "./BookmarkPage";
import ServicesPage from "./ServicesPage";
import TackleShopsPage from "./TackleShopsPage";
import AnglerSafetyPage from "./AnglerSafetyPage";
import JustTidesPage from "./JustTidesPage";
import SeaViewsPage from "./SeaViewsPage";
import FishingConditionsPage from "./FishingConditionsPage";
import FishingMarksPage from "./FishingMarksPage";
import AboutPage from "./AboutPage";
import FixturesPage from "./FixturesPage";
import OfficersPage from "./OfficersPage";
import DocumentsPage from "./DocumentsPage";
import MembershipPage from "./MembershipPage";
import ContactPage from "./ContactPage";
import PaymentsPage from "./PaymentsPage";
import NewsPage from "./NewsPage";
import GalleryPage from "./GalleryPage";
import YouTubePage from "./YouTubePage";
import { CreditCard } from "lucide-react";





// static data moved outside component to avoid recreating on every render
const navItems = [
  { label: "Home", to: "/" },
  { label: "About", to: "/about" },
  { label: "Fixtures", to: "/fixtures" },
  { label: "Sea Conditions", to: "/fishing-conditions" },
  { label: "Places to Fish in Cornwall", to: "/fishing-marks" },
  { label: "Current Winners", to: "/awards" },
  { label: "Officers", to: "/officers" },
  { label: "Records", children: [
    { label: "C&R Records",           to: "/records"        },
    { label: "Official Club Records", to: "/rsaa-records"   },
    { label: "Junior Records",        to: "/junior-records" },
    { label: "Junior C&R Records",    to: "/junior-catch-release" },
    { label: "Awards & Fish of Month", to: "/awards"        },
    { label: "Ray & Bass Hunt",        to: "/ray-bass-hunt" },
  ]},
  { label: "Species Hunt", to: "/species-hunt" },
  { label: "Club Rules & Documents", children: [
    { label: "Meeting Agenda & Minutes",        to: "/upcoming-meeting" },
    { label: "Weighing Scales Policy",          to: "/scales-policy"   },
    { label: "Weighing & Measuring Procedures", to: "/weigh-measure"   },
    { label: "Cornwall Sea Angling Strategy (Draft)", to: "/sea-angling-strategy" },
    { label: "RSAA Safeguarding Policy",        href: "/assets/Safeguarding_Policy.pdf"               },
    { label: "Club Rules",                      href: "/assets/rsaa_club_rules.pdf"                   },
    { label: "RSAA Constitution",               href: "/assets/rsaa_constitution.pdf"                 },
    { label: "Club Fixtures",                   href: "/assets/club_fixtures_2026.pdf"                },
    { label: "Boat Risk Assessments",           href: "/assets/Boat_Risk_Assessments.pdf"             },
    { label: "Boat Safety Report",              href: "/assets/rsaa_boat_safety_report.pdf"           },
    { label: "Shore Risk Assessment",           href: "/assets/Shore_Fishing_Risk_Assessment.pdf"     },
    { label: "Month Long Boat Competitions",    href: "/assets/month_long_boat_comps.pdf"             },
    { label: "Month Long Shore Competitions",   href: "/assets/month_long_shore_comps.pdf"            },
    { label: "RSAA Species Hunt Rules",         href: "/assets/rsaa_species_hunt_rules.pdf"           },
  ]},
  { label: "Sponsors & Merchandise", children: [
    { label: "Club Sponsors",     to: "/sponsors"    },
    { label: "Club Merchandise", to: "/merchandise" },
  ]},
  { label: "Tackle, Bait & Services", children: [
    { label: "Local Services",     to: "/services"     },
    { label: "Tackle & Bait Shops", to: "/tackle-shops" },
  ]},
  { label: "Angler's Tools", children: [
    { label: "Specimen Calculator",                  to: "/cfsa-specimen"          },
    { label: "Competitions Leaderboard Calculator", to: "/competition-calculator" },
    { label: "Angler Safety & what3words",          to: "/angler-safety"          },
    { label: "Just Tides",                          to: "/just-tides"             },
    { label: "Sea Views — Live Webcams",            to: "/sea-views"              },
    { label: "Detailed Weather Forecast",           href: "https://xcweather.co.uk/" },
  ]},
  { label: "Media", children: [
    { label: "News",             to: "/news"    },
    { label: "Gallery",          to: "/gallery" },
    { label: "YouTube Channels", to: "/youtube" },
  ]},
  { label: "Club Payments", to: "/payments" },
  { label: "Contact", to: "/contact" },
];



const officers = [
  "President: John Munday",
  "Chairman: Mark Kaczmarek",
  "Vice Chairman: Chris Longman",
  "Hon. Secretary: Tim Clotworthy",
  "Hon.Treasurer: Graeme Hicks",
  "Fish Recorder: Chris Wills",
  "Assistant Fish Recorder: Brian Collick",
  "Shore Competition Secretary: Brian Collick",
  "Boat Competition Secretary: Edward Hicks",
  "Youth Officer: Simon Thomas",
  "Welfare Officer: Nick Ward",
  "Cup Custodian: ",
  "Social Media Secretary: Graeme Hicks",
  "Conservation Officer: Brian Collick",
  "Webmaster: Andrew Searle",
];


const fixtureCards = [
  {
    date: "Sun 1st – Sat 28th February",
    isoDate: "2026-02-01",
    type: "Rover Competition",
    venue: "Rover – best % of 2 fish of any species, 1 of each species",
    time: "Start 8:00am Sun 1st Feb · Weigh-in by 9:30pm Sat 28th Feb",
    venueType: "shore",
    rules: "/assets/month_long_shore_comps.pdf",
    monthLong: true,
  },
  {
    date: "Sun 29th March",
    isoDate: "2026-03-29",
    type: "Easter Competition",
    venue: "Shore – any venue",
    time: "Start 8:00am · Last Fish Sun 12th Apr 9:30pm · Best total % from 4 different species",
    venueType: "shore",
    rules: "/assets/competition_rules/RSAA_Easter_comp_rules.pdf",
  },
  {
    date: "Sun 17th May",
    isoDate: "2026-05-17",
    type: "Colin Rule (1)",
    venue: "Weigh-in: Sea View, Treskerby, Redruth, TR15 1RG",
    time: "Start 8:00am · Weigh-in 9:30pm · High Tide 6:30pm",
    venueType: "shore",
    rules: "/assets/competition_rules/RSAA_Colin_Rule_Final.pdf",
  },
  {
    date: "Mon 1st – Tue 30th June",
    isoDate: "2026-06-01",
    type: "Small Boats / Dinghy Competition",
    venue: "Rover – privately owned boats/watercraft under 8m",
    time: "Start 8am Mon 1st Jun · Weigh-in by 9:30pm 30th Jun · Best % 2 species",
    venueType: "boat",
  },
  {
    date: "Sun 7th June",
    isoDate: "2026-06-07",
    type: "Half Day Club Boat Trip",
    venue: "South Quay · Che Sara Sara (Dave Trebilcock)",
    time: "Start 9:00am · Finish 1:00pm",
    venueType: "boat",
  },
  {
    date: "Sun 14th June",
    isoDate: "2026-06-14",
    type: "Colin Rule (2)",
    venue: "Weigh-in: Sea View, Treskerby, Redruth, TR15 1RG",
    time: "Start 8:00am · Weigh-in 9:30pm · High Tide 7:50pm",
    venueType: "shore",
    rules: "/assets/competition_rules/RSAA_Colin_Rule_Final.pdf",
  },
  {
    date: "Sat 27th June",
    isoDate: "2026-06-27",
    type: "Half Day Club Boat Trip",
    venue: "North Quay · Che Sara Sara (Dave Trebilcock)",
    time: "Start 9:00am · Finish 1:00pm",
    venueType: "boat",
  },
  {
    date: "Sat 1st August",
    isoDate: "2026-08-01",
    type: "Bream Competition",
    venue: "Total cumulative percentage for up to two bream species (maximum one fish from each species). Where both one-species and two-species combinations are possible, the two-species combination takes precedence.",
    time: "Normal weigh-in rules apply",
    venueType: "shore",
    rules: "/assets/month_long_shore_comps.pdf",
    monthLong: true,
  },
  {
    date: "Sun 23rd August",
    isoDate: "2026-08-23",
    type: "Full Day Club Boat Trip",
    venue: "Bite Adventures (Robin Chapman)",
    time: "Full day trip – details to follow",
    venueType: "boat",
  },
  {
    date: "Sun 30th August",
    isoDate: "2026-08-30",
    type: "Colin Rule (3)",
    venue: "Weigh-in: Sea View, Treskerby, Redruth, TR15 1RG",
    time: "Start 8:00am · Weigh-in 9:30pm · High Tide 7:35pm",
    venueType: "shore",
    rules: "/assets/competition_rules/RSAA_Colin_Rule_Final.pdf",
  },
  {
    date: "Sun 27th September",
    isoDate: "2026-09-27",
    type: "Float and Spin Competition",
    venue: "Weigh-in: Sea View, Treskerby, Redruth, TR15 1RG",
    time: "Start 8:00am · Weigh-in 9:30pm · High Tide 6:30pm",
    venueType: "shore",
    rules: "/assets/competition_rules/RSAA_Float_Spin_Competition_2026.pdf",
  },
  {
    date: "Thu 1st October",
    isoDate: "2026-10-01",
    type: "Ray and Bass Competition",
    venue: "TBC – see separate sheet",
    time: "See separate sheet for full details",
    venueType: "shore",
    rules: "/assets/month_long_shore_comps.pdf",
    monthLong: true,
  },
  {
    date: "Sat 17th October",
    isoDate: "2026-10-17",
    type: "Beach Competition",
    venue: "TBC – decided 2 days before",
    time: "Meet 6:30pm · Fish 7:00pm · Weigh-in midnight · High Tide 10:00pm",
    venueType: "shore",
    rules: "/assets/competition_rules/RSAA_Night_Beach_Competition_2026.pdf",
  },
  {
    date: "Sun 15th November",
    isoDate: "2026-11-15",
    type: "Conger Competition",
    venue: "TBC – decided 2 days before",
    time: "Start 4:30pm · Finish 9:00pm",
    venueType: "shore",
    rules: "/assets/competition_rules/RSAA_Conger_Competition_2026.pdf",
  },
  {
    date: "Sun 13th December",
    isoDate: "2026-12-13",
    type: "Colin Rule (4)",
    venue: "Weigh-in: Sea View, Treskerby, Redruth, TR15 1RG",
    time: "Start 8:00am · Weigh-in 9:30pm · High Tide 7:45pm",
    venueType: "shore",
    rules: "/assets/competition_rules/RSAA_Colin_Rule_Final.pdf",
  },
];

const trophyCategories = [
  {
    label: "Club Competition Trophies – Shore",
    trophies: [
      { name: "South Crofty Cup", award: "Best Percentage 4 Species – Easter Competition" },
      { name: "Colin Rule", award: "Best Percentage 4 Species – Colin Rule Competition" },
      { name: "Mike Osman Cup", award: "Heaviest Fish – Colin Rule Competition" },
      { name: "Secretary's Cup", award: "Heaviest Bag – Float and Spin" },
      { name: "Rosewarne Trophy", award: "Heaviest Bag Runner Up – Float and Spin" },
      { name: "Phil Triniman Cup", award: "Heaviest Bass – All Night Beach" },
      { name: "Jack Toms Cup", award: "Heaviest Bag – All Night Beach" },
      { name: "Eric Thomas Cup", award: "Heaviest Bag by a Lady – All Night Beach" },
      { name: "Warren Mitchell Trophy", award: "Heaviest Conger – Fixed Venue Conger Competition" },
    ],
  },
  {
    label: "One Day Boat Competition",
    trophies: [
      { name: "Sports Kit Cup", award: "Best Percentage Specimen" },
      { name: "CV Matthews Cup", award: "Best Aggregate Percentage from Different Species" },
    ],
  },
  {
    label: "Junior Day Competition – Shore",
    trophies: [
      { name: "Kayece Cup", award: "Best Percentage Specimen" },
      { name: "Bob Sawford Trophy", award: "Heaviest Bag of Fish" },
    ],
  },
  {
    label: "Senior Shore",
    trophies: [
      { name: "Tom Altree Cup", award: "Heaviest Bass" },
      { name: "Vesta Heather Cup", award: "Heaviest Pollock" },
      { name: "RSAA Heaviest Mackerel Cup", award: "Heaviest Mackerel" },
      { name: "Ken Munday Memorial Trophy", award: "Best Specimen Flatfish" },
      { name: "Rafidae Trophy", award: "Heaviest Skate or Ray" },
      { name: "Intrepid Cup", award: "Heaviest Mullet" },
      { name: "Wrasse Trophy", award: "Heaviest Wrasse" },
      { name: "Hardy Cup", award: "Best Specimen by a Member (seniors only, not previously won a perpetual trophy)" },
      { name: "A.B.U. Cup", award: "Best Specimen caught surf-fishing from a North Coast Beach" },
      { name: "Noel Bradbury Cup", award: "Heaviest Bass caught spinning with an artificial lure" },
      { name: "Fred Hollis Shield", award: "Heaviest Bass caught from Praa Sands Beach" },
    ],
  },
  {
    label: "Junior Shore",
    trophies: [
      { name: "Browne Cup", award: "Heaviest Bass" },
      { name: "Francis Johns Cup", award: "Heaviest Pollock" },
      { name: "Hugh Pengelly Cup", award: "Heaviest Mackerel" },
      { name: "Tredinnick Cup", award: "Best Specimen Flatfish" },
      { name: "One and All Sports Trophy", award: "Heaviest Wrasse" },
      { name: "Knuckey Cup", award: "Best Specimen Mullet" },
      { name: "Holiday Boat Centre Cup", award: "Best Other Specimen (excludes species covered by the other Junior Shore trophies)" },
      { name: "S.F.B. Trophy", award: "Best Specimen by a junior under 13 at beginning of Club year" },
    ],
  },
  {
    label: "Ladies Shore",
    trophies: [
      { name: "Arthur Paul Cup", award: "Heaviest Bass" },
      { name: "Andrew Cup", award: "Heaviest Mackerel" },
      { name: "Norman Tucker Cup", award: "Best Specimen Flatfish" },
      { name: "Altree and Channon Cup", award: "Best Other Specimen (excludes species covered by the other Ladies Shore trophies)" },
    ],
  },
  {
    label: "Boat",
    trophies: [
      { name: "Opie and Wakeling Cup", award: "Heaviest Pollock" },
      { name: "R.S.A.A. Cup", award: "Best Other Specimen (excludes fish eligible for any other senior Boat or Boat & Shore trophy)" },
      { name: "Thomas William Cup", award: "Best Specimen caught on a Club boat trip" },
      { name: "Stephens and Viant Cup", award: "Heaviest Bream" },
      { name: "Ron Stevens Cup", award: "Best junior specimen not winning a Senior Boat Trophy" },
    ],
  },
  {
    label: "Boat & Shore",
    trophies: [
      { name: "Conger Cup", award: "Best Specimen Conger" },
      { name: "Phil Kevern Cup", award: "Best Specimen Greater Spotted Dogfish" },
      { name: "Tope Trophy", award: "Best Specimen Tope" },
      { name: "Shirley-Anne Cup", award: "Best aggregate percentage by a Junior Member from 3 different species" },
      { name: "Sandy Underwood Memorial Shield", award: "Most species (over CFSA 1 day weights) from boat or shore" },
    ],
  },
  {
    label: "Champion Anglers",
    trophies: [
      { name: "Mackeson Trophy", award: "Club Champion – Best aggregate percentage by a Senior Member from 5 species. with 2 or 3 fish from boat and/or 2 or 3 from shore" },
      { name: "Clinton Social Club Cup", award: "Shore Champion – Best aggregate percentage from 5 different species" },
      { name: "Lovett Cup", award: "Boat Champion – Best aggregate percentage from 5 different species" },
    ],
  },
];



const documentLinks = [
  { name: "RSAA Safeguarding Policy", file: "/assets/Safeguarding_Policy.pdf" },
  { name: "Club Rules", file: "/assets/rsaa_club_rules.pdf" },
  { name: "RSAA Constitution", file: "/assets/rsaa_constitution.pdf" },
  { name: "Club Fixtures", file: "/assets/club_fixtures_2026.pdf" },
  { name: "Boat Risk Assessments", file: "/assets/Boat_Risk_Assessments.pdf" },
  { name: "Boat Safety Report", file: "/assets/rsaa_boat_safety_report.pdf" },
  { name: "Shore Risk Assessment", file: "/assets/Shore_Fishing_Risk_Assessment.pdf" },
  { name: "Month Long Boat Competitions", file: "/assets/month_long_boat_comps.pdf" },
  { name: "Month Long Shore Competitions", file: "/assets/month_long_shore_comps.pdf" },
  { name: "RSAA Species Hunt Rules", file: "/assets/rsaa_species_hunt_rules.pdf" },
  { name: "Weighing Scales Policy", file: "/assets/rsaa_weighing_scales_policy.pdf" },
  { name: "Use of Social Media by RSAA and Juniors - consent form", file: "/assets/rsaa_parential_consent_form.pdf" },
];



function CatchesSlideshow({ lang }) {
  const tt = (s) => (lang === 'kw' && translations.kw.strings[s]) ? translations.kw.strings[s] : s;
  const [photos, setPhotos] = useState([]);
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    fetch("/.netlify/functions/fb-photos")
      .then((r) => r.json())
      .then((data) => {
        if (data.photos && data.photos.length > 0) {
          const cutoff = new Date();
          cutoff.setDate(cutoff.getDate() - 28);
          const dedup = (arr) => { const seen = new Set(); return arr.filter((p) => { if (seen.has(p.full)) return false; seen.add(p.full); return true; }); };
          const recent = data.photos.filter((p) => p.created && new Date(p.created) >= cutoff);
          setPhotos(dedup(recent.length > 0 ? recent : data.photos));
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (photos.length === 0 || paused) return;
    const timer = setInterval(() => {
      setCurrent((i) => (i + 1) % photos.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [photos.length, paused]);

  const prev = () => setCurrent((i) => (i > 0 ? i - 1 : photos.length - 1));
  const next = () => setCurrent((i) => (i < photos.length - 1 ? i + 1 : 0));

  if (photos.length === 0) return null;

  const photo = photos[current];

  return (
    <section className="relative overflow-hidden bg-slate-950 px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
      <div className="relative mx-auto max-w-7xl">
        <div className="max-w-3xl mb-8">
          <p className="text-sm font-semibold uppercase tracking-[0.3em] text-cyan-300">{tt("Members' catches")}</p>
          <h2 className="mt-4 text-3xl font-bold tracking-tight sm:text-4xl">{tt("Latest from the club")}</h2>
        </div>

        <div
          className="relative overflow-hidden rounded-[2rem] bg-slate-900"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          {/* Image */}
          <div className="aspect-[16/9] sm:aspect-[21/9] bg-slate-950">
            <img
              key={photo.id}
              src={photo.full}
              alt={photo.caption || "Member catch"}
              className="h-full w-full object-contain transition-opacity duration-700"
            />
          </div>

          {/* Gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-transparent to-transparent" />

          {/* Caption */}
          {photo.caption && (
            <div className="absolute bottom-0 inset-x-0 px-6 pb-6">
              <p className="text-sm leading-6 text-slate-200 line-clamp-2">{photo.caption}</p>
            </div>
          )}

          {/* Prev / Next */}
          <button
            type="button"
            onClick={prev}
            className="absolute left-4 top-1/2 -translate-y-1/2 rounded-2xl border border-cyan-400/40 bg-slate-900/90 px-4 py-3 text-cyan-300 shadow-lg shadow-black/40 backdrop-blur transition hover:bg-cyan-400/20 hover:border-cyan-300"
          >
            <ChevronLeft className="h-7 w-7" />
          </button>
          <button
            type="button"
            onClick={next}
            className="absolute right-4 top-1/2 -translate-y-1/2 rounded-2xl border border-cyan-400/40 bg-slate-900/90 px-4 py-3 text-cyan-300 shadow-lg shadow-black/40 backdrop-blur transition hover:bg-cyan-400/20 hover:border-cyan-300"
          >
            <ChevronRight className="h-7 w-7" />
          </button>

        </div>

        {/* Page counter */}
        <div className="mt-3 flex justify-center">
          <div className="rounded-xl border border-white/10 bg-slate-900/80 px-3 py-1.5 backdrop-blur">
            <span className="text-sm font-semibold text-cyan-300">{current + 1}</span>
            <span className="text-sm text-slate-400"> / {photos.length}</span>
          </div>
        </div>
      </div>
    </section>
  );
}

const riskAssessments = {
  shore: { label: "Shore Fishing Risk Assessment", file: "/assets/Shore_Fishing_Risk_Assessment.pdf" },
  boat: { label: "Boat Risk Assessment", file: "/assets/Boat_Risk_Assessments.pdf" },
};

function RiskAssessmentTabs({ lang }) {
  const tt = (s) => (lang === 'kw' && translations.kw.strings[s]) ? translations.kw.strings[s] : s;
  const [active, setActive] = useState("shore");
  const current = riskAssessments[active];
  return (
    <div className="mt-6 inline-flex flex-col rounded-2xl border border-amber-400/40 bg-amber-400/10 overflow-hidden">
      <div className="flex">
        {Object.entries(riskAssessments).map(([key, ra]) => (
          <button
            key={key}
            onClick={() => setActive(key)}
            className={`px-5 py-2.5 text-xs font-semibold transition ${
              active === key
                ? "bg-amber-400/20 text-amber-200"
                : "text-amber-400/70 hover:text-amber-300"
            }`}
          >
            {tt(key === "shore" ? "Shore Competitions" : "Boat Competitions")}
          </button>
        ))}
      </div>
      <div className="flex items-center gap-2.5 border-t border-amber-400/20 px-5 py-3">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4 shrink-0 text-amber-300">
          <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
        </svg>
        <span className="text-sm text-amber-200">
          {tt("Members competing in")} {tt(active === "shore" ? "Shore Competitions" : "Boat Competitions")} {tt("competitions must read the")}{" "}
          <a
            href={current.file}
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold text-amber-200 underline underline-offset-2 hover:text-white transition"
          >
            {tt(current.label)}
          </a>{" "}
          {tt("before competing")}
        </span>
      </div>
    </div>
  );
}

function NextCompetitionBanner({ lang }) {
  const tt = (s) => (lang === 'kw' && translations.kw.strings[s]) ? translations.kw.strings[s] : s;
  const [countdown, setCountdown] = useState(null);

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const next = fixtureCards.find((f) => f.isoDate && new Date(f.isoDate) >= today);

  useEffect(() => {
    if (!next) return;
    const target = new Date(next.isoDate);
    target.setHours(8, 0, 0, 0); // competitions typically start at 8am

    const update = () => {
      const diff = target - new Date();
      if (diff <= 0) { setCountdown({ days: 0, hours: 0, minutes: 0, seconds: 0 }); return; }
      setCountdown({
        days:    Math.floor(diff / 86400000),
        hours:   Math.floor((diff % 86400000) / 3600000),
        minutes: Math.floor((diff % 3600000) / 60000),
        seconds: Math.floor((diff % 60000) / 1000),
      });
    };
    update();
    const id = setInterval(update, 1000);
    return () => clearInterval(id);
  }, [next]);

  if (!next || !countdown) return null;

  const isToday = countdown.days === 0 && countdown.hours === 0;
  const isSoon  = countdown.days <= 7;

  const Unit = ({ value, label }) => (
    <div className="flex flex-col items-center">
      <span className={`font-extrabold tabular-nums ${isSoon ? "text-4xl sm:text-5xl text-amber-300" : "text-3xl sm:text-4xl text-cyan-300"}`}>
        {String(value).padStart(2, "0")}
      </span>
      <span className={`text-[10px] uppercase tracking-widest mt-0.5 ${isSoon ? "text-amber-200/70" : "text-cyan-300/60"}`}>{label}</span>
    </div>
  );

  return (
    <div
      className={`relative overflow-hidden border-b ${
        isSoon
          ? "border-amber-400/50 bg-gradient-to-r from-amber-500/25 via-amber-400/10 to-transparent shadow-[0_0_50px_-10px_rgba(251,191,36,0.55)]"
          : "border-cyan-400/40 bg-gradient-to-r from-cyan-500/20 via-cyan-400/5 to-transparent shadow-[0_0_35px_-14px_rgba(34,211,238,0.45)]"
      }`}
    >
      <div className={`absolute inset-x-0 top-0 h-[3px] bg-gradient-to-r ${isSoon ? "from-transparent via-amber-400 to-transparent" : "from-transparent via-cyan-400 to-transparent"}`} />
      {isSoon && <div className="pointer-events-none absolute inset-0 animate-pulse bg-amber-400/5" />}
      <div className="relative mx-auto max-w-7xl px-4 py-5 sm:px-6 lg:px-8">
        <div className="flex flex-wrap items-center justify-between gap-5">
          <div className="flex items-start gap-4">
            <div
              className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl ${
                isSoon ? "bg-amber-400/20 ring-2 ring-amber-400/50" : "bg-cyan-400/15 ring-2 ring-cyan-400/30"
              }`}
            >
              <CalendarDays className={`h-6 w-6 ${isSoon ? "text-amber-300" : "text-cyan-300"}`} />
            </div>
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <p className={`text-xs font-bold uppercase tracking-widest ${isSoon ? "text-amber-300" : "text-cyan-300"}`}>
                  {isSoon ? tt("Coming Up Soon") : tt("Next Competition")}
                </p>
                {next.monthLong && (
                  <span className="inline-flex items-center rounded-full border border-fuchsia-400/40 bg-fuchsia-400/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-fuchsia-300">
                    {tt("Month Long Competition")}
                  </span>
                )}
              </div>
              <p className="mt-0.5 text-lg font-bold text-white sm:text-xl">
                {tt(next.type)} · <span className="font-semibold text-slate-300">{next.date}</span>
              </p>
              {(next.venue || next.time) && (
                <p className="mt-1 max-w-2xl text-xs text-slate-300 sm:text-sm">
                  {next.venue}
                  {next.venue && next.time ? " · " : ""}
                  {next.time}
                </p>
              )}
              {(next.rules || next.venueType) && (
                <div className="mt-2.5 flex flex-wrap gap-4">
                  {next.rules && (
                    <a
                      href={next.rules}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-xs font-semibold text-cyan-300 hover:text-cyan-100 transition"
                    >
                      <FileText className="h-3.5 w-3.5 shrink-0" />
                      {tt("Competition Rules")}
                    </a>
                  )}
                  {next.venueType && (
                    <a
                      href={riskAssessments[next.venueType].file}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-400 hover:text-amber-200 transition"
                    >
                      <ShieldAlert className="h-3.5 w-3.5 shrink-0" />
                      {tt(riskAssessments[next.venueType].label)}
                    </a>
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-4">
            {isToday ? (
              <span className="animate-pulse rounded-full bg-amber-400 px-5 py-2 text-sm font-bold text-slate-950 shadow-lg shadow-amber-400/50">
                {tt("Happening Today!")}
              </span>
            ) : (
              <div
                className={`flex items-center gap-3 rounded-2xl px-4 py-3 ring-1 sm:gap-4 ${
                  isSoon ? "bg-slate-950/50 ring-amber-400/40" : "bg-slate-950/40 ring-cyan-400/25"
                }`}
              >
                <Unit value={countdown.days}    label={tt("days")}  />
                <span className={`font-bold text-xl mb-3 ${isSoon ? "text-amber-400/50" : "text-cyan-400/40"}`}>:</span>
                <Unit value={countdown.hours}   label={tt("hrs")}   />
                <span className={`font-bold text-xl mb-3 ${isSoon ? "text-amber-400/50" : "text-cyan-400/40"}`}>:</span>
                <Unit value={countdown.minutes} label={tt("mins")}  />
                <span className={`font-bold text-xl mb-3 ${isSoon ? "text-amber-400/50" : "text-cyan-400/40"}`}>:</span>
                <Unit value={countdown.seconds} label={tt("secs")}  />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function FishRecorderBanner({ lang }) {
  const tt = (s) => (lang === 'kw' && translations.kw.strings[s]) ? translations.kw.strings[s] : s;

  return (
    <div className="relative overflow-hidden border-b border-cyan-400/30 bg-cyan-400/10">
      <div className="mx-auto max-w-7xl px-4 py-4 sm:px-6 lg:px-8">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            <Fish className="h-5 w-5 shrink-0 text-cyan-400 mt-0.5" />
            <div>
              <p className="text-xs font-bold uppercase tracking-widest text-cyan-300">
                {tt("Submit Your Catch — Within 48 Hours")}
              </p>
              <p className="mt-1 flex flex-wrap items-center gap-x-1 text-sm font-semibold text-white">
                {tt("Fish Recorder")}: Chris Wills ·{" "}
                <a
                  href="https://wa.me/447771646872"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-cyan-300 transition hover:text-cyan-200"
                >
                  <MessageCircle className="h-4 w-4" /> {tt("Contact via WhatsApp")}
                </a>
                <span className="text-slate-400"> &nbsp;|&nbsp; </span>
                {tt("Secondary Fish Recorder")}: Brian Collick · 01209 217617 / 07759 332750
              </p>
            </div>
          </div>
          <Link
            to="/weigh-measure"
            className="shrink-0 rounded-lg border border-cyan-300/40 bg-cyan-400/10 px-4 py-2 text-sm font-semibold text-cyan-200 transition hover:bg-cyan-400/20"
          >
            {tt("Submission Details")} →
          </Link>
        </div>
      </div>
    </div>
  );
}

// Start each newly opened page at the top. Skipped for back/forward (POP) so the
// browser can return you to where you were, and for "/" where HomeRoute scrolls itself.
function ScrollToTop() {
  const { pathname } = useLocation();
  const navigationType = useNavigationType();
  useEffect(() => {
    if (navigationType !== "POP" && pathname !== "/") window.scrollTo(0, 0);
  }, [pathname, navigationType]);
  return null;
}

// Background video that only downloads once it's near the viewport. Reduced-motion
// and data-saver visitors get the poster frame instead.
function LazyBackgroundVideo({ src, poster, className }) {
  const ref = useRef(null);
  const [load, setLoad] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches || navigator.connection?.saveData) return;
    if (!("IntersectionObserver" in window)) { setLoad(true); return; }
    const io = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { setLoad(true); io.disconnect(); }
    }, { rootMargin: "300px" });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <video
      ref={ref}
      src={load ? src : undefined}
      poster={poster}
      autoPlay
      muted
      loop
      playsInline
      preload="none"
      aria-hidden="true"
      className={className}
    />
  );
}

function HomeOnly({ children }) {
  return useLocation().pathname === '/' ? children : null;
}

function HomeRoute({ section, homeElement, scrollToSection }) {
  const location = useLocation();

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      if (!section) {
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        scrollToSection(section);
      }
    }, 50);
    return () => window.clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key, section]);

  return homeElement;
}

export default function App() {
  const [lang, setLang] = useState('en');
  const t = (key) => translations[lang][key];
  const tt = (s) => (lang === 'kw' && translations.kw.strings[s]) ? translations.kw.strings[s] : s;
  const [openDropdown, setOpenDropdown] = useState(null);
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactMessage, setContactMessage] = useState("");
  const [contactSent, setContactSent] = useState(false);
  const [contactError, setContactError] = useState(false);
  const [membershipTab, setMembershipTab] = useState("adult");
  const [officersOpen, setOfficersOpen] = useState(false);
  const [fixturesOpen, setFixturesOpen] = useState(false);
  const [documentsOpen, setDocumentsOpen] = useState(false);
  const [newsOpen, setNewsOpen] = useState(false);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [footerOpenGroup, setFooterOpenGroup] = useState(null);
  const [rulesAgreed, setRulesAgreed] = useState(false);
  const [memberName, setMemberName] = useState("");
  const [memberAddress, setMemberAddress] = useState("");
  const [memberEmail, setMemberEmail] = useState("");
  const [memberMobile, setMemberMobile] = useState("");
  const [juniorRulesAgreed, setJuniorRulesAgreed] = useState(false);
  const [bacsSubmitted, setBacsSubmitted] = useState(false);
  const [bacsJuniorSubmitted, setBacsJuniorSubmitted] = useState(false);
  const [juniorName, setJuniorName] = useState("");
  const [juniorDob, setJuniorDob] = useState("");
  const [juniorAddress, setJuniorAddress] = useState("");
  const [juniorMobile, setJuniorMobile] = useState("");
  const [parentName, setParentName] = useState("");
  const [parentEmail, setParentEmail] = useState("");
  const [parentMobile, setParentMobile] = useState("");

  const currentYear = new Date().getFullYear();
  const juniorAgeOnJan1 = juniorDob ? (() => {
    const jan1 = new Date(currentYear, 0, 1);
    const birth = new Date(juniorDob);
    let age = jan1.getFullYear() - birth.getFullYear();
    const m = jan1.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && jan1.getDate() < birth.getDate())) age--;
    return age;
  })() : null;
  const juniorEligible = juniorAgeOnJan1 !== null && juniorAgeOnJan1 <= 16;

  const membershipReady = memberName.trim() && memberAddress.trim() && memberEmail.trim() && memberMobile.trim() && rulesAgreed;
  const juniorMembershipReady = juniorName.trim() && juniorDob && juniorEligible && juniorAddress.trim() && parentName.trim() && parentEmail.trim() && parentMobile.trim() && juniorRulesAgreed;

  const handleSendEnquiry = (e) => {
    e.preventDefault();
    setContactError(false);
    const encode = (data) =>
      Object.keys(data)
        .map((key) => encodeURIComponent(key) + "=" + encodeURIComponent(data[key]))
        .join("&");
    fetch("/", {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: encode({ "form-name": "contact", name: contactName, email: contactEmail, message: contactMessage }),
    })
      .then(() => {
        setContactSent(true);
        setContactName("");
        setContactEmail("");
        setContactMessage("");
      })
      .catch(() => setContactError(true));
  };

  const submitMembershipToNetlify = () => {
    const encode = (data) =>
      Object.keys(data).map((k) => encodeURIComponent(k) + "=" + encodeURIComponent(data[k])).join("&");
    fetch("/", {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: encode({ "form-name": "membership", name: memberName, address: memberAddress, email: memberEmail, mobile: memberMobile, rulesAgreed: "Yes – agreed to club rules and constitution" }),
    }).catch(() => {});
  };

  const handleMembershipClick = (e) => {
    if (!membershipReady) { e.preventDefault(); return; }
    submitMembershipToNetlify();
  };

  const handleBacsSubmit = () => {
    submitMembershipToNetlify();
    setBacsSubmitted(true);
  };

  const submitJuniorMembershipToNetlify = () => {
    const encode = (data) =>
      Object.keys(data).map((k) => encodeURIComponent(k) + "=" + encodeURIComponent(data[k])).join("&");
    fetch("/", {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: encode({ "form-name": "junior-membership", name: juniorName, dob: juniorDob, address: juniorAddress, mobile: juniorMobile, parentName, parentEmail, parentMobile, rulesAgreed: "Yes – agreed to club rules and constitution" }),
    }).catch(() => {});
  };

  const handleJuniorMembershipClick = (e) => {
    if (!juniorMembershipReady) { e.preventDefault(); return; }
    submitJuniorMembershipToNetlify();
  };

  const handleJuniorBacsSubmit = () => {
    submitJuniorMembershipToNetlify();
    setBacsJuniorSubmitted(true);
  };

  const scrollToSection = (sectionId) => {
    setOpenDropdown(null);
    const el = document.getElementById(sectionId);
    if (el) {
      // Measure only the nav bar row, not the install strip inside <header>
      const navBar = document.querySelector('header > div:first-child');
      const headerHeight = navBar?.offsetHeight ?? document.querySelector('header')?.offsetHeight ?? 80;
      const top = el.getBoundingClientRect().top + window.scrollY - headerHeight;
      window.scrollTo({ top, behavior: "smooth" });
    }
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") setOpenDropdown(null);
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Horizontally scrolling tab strip: track whether there's more to see either side
  const navScrollRef = useRef(null);
  const [navCanScroll, setNavCanScroll] = useState({ left: false, right: false });
  const [navDropdownPos, setNavDropdownPos] = useState(null);
  const navScrollAtOpen = useRef(0);
  const updateNavCanScroll = () => {
    const el = navScrollRef.current;
    if (!el) return;
    const left = el.scrollLeft > 2;
    const right = el.scrollLeft + el.clientWidth < el.scrollWidth - 2;
    setNavCanScroll((prev) => (prev.left === left && prev.right === right ? prev : { left, right }));
  };
  useEffect(() => {
    const el = navScrollRef.current;
    if (!el) return;
    const ro = new ResizeObserver(updateNavCanScroll);
    ro.observe(el);
    updateNavCanScroll();
    return () => ro.disconnect();
  }, []);
  const handleNavScroll = () => {
    updateNavCanScroll();
    // A dropdown is anchored to its tab, so close it once the tab slides away
    const moved = Math.abs((navScrollRef.current?.scrollLeft ?? 0) - navScrollAtOpen.current);
    if (openDropdown && openDropdown !== 'lang' && moved > 24) setOpenDropdown(null);
  };
  const scrollNav = (dir) => {
    const el = navScrollRef.current;
    if (el) el.scrollBy({ left: dir * el.clientWidth * 0.7, behavior: 'smooth' });
  };
  const openNavDropdown = (e, label) => {
    e.stopPropagation();
    if (openDropdown === label) { setOpenDropdown(null); return; }
    e.currentTarget.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    navScrollAtOpen.current = navScrollRef.current?.scrollLeft ?? 0;
    const r = e.currentTarget.getBoundingClientRect();
    const panelWidth = 256;
    setNavDropdownPos({ top: r.bottom + 8, left: Math.max(8, Math.min(r.left, window.innerWidth - panelWidth - 8)) });
    setOpenDropdown(label);
  };
  const scrollNavItems = navItems.filter((item) => item.to !== '/' && item.to !== '/contact');
  const openNavGroup = navItems.find((item) => item.children && item.label === openDropdown);

const footerSection = (title, node, wrapperClassName = "") => {
    const isOpen = footerOpenGroup === title;
    return (
      <div className={wrapperClassName}>
        <button
          type="button"
          onClick={() => setFooterOpenGroup(isOpen ? null : title)}
          className="flex w-full items-center justify-between gap-2 text-left lg:pointer-events-none lg:cursor-default"
        >
          <h3 className="text-xs font-bold uppercase tracking-widest text-cyan-300">{tt(title)}</h3>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className={`h-5 w-5 shrink-0 text-cyan-300 transition-transform duration-300 lg:hidden ${isOpen ? "rotate-180" : ""}`}><path fillRule="evenodd" d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06z" clipRule="evenodd" /></svg>
        </button>
        <div className={`${isOpen ? "block" : "hidden"} lg:block mt-4`}>
          {node}
        </div>
      </div>
    );
  };

// Site-wide header — sticky on every page
const siteHeader = (
    <header className="sticky top-0 z-50 bg-gradient-to-r from-cyan-950 via-sky-900 to-blue-950 shadow-2xl shadow-blue-950/70 ring-1 ring-black/5 border-b-2 border-cyan-300/70">
      <div className="flex w-full flex-wrap items-center justify-between gap-x-1 gap-y-2 px-3 py-2 sm:px-4 lg:px-6 2xl:flex-nowrap">
        <div className="flex min-w-0 items-center gap-3 sm:gap-4 lg:gap-4">
          <Link
            to="/"
            className="relative flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl border border-cyan-300/40 bg-white/95 p-2 shadow-[0_0_0_2px_rgba(2,6,23,0.4),0_10px_16px_-2px_rgba(2,6,23,1),0_28px_48px_rgba(2,6,23,0.95),0_70px_110px_rgba(2,6,23,0.85),0_110px_170px_rgba(2,6,23,0.65)] backdrop-blur transition duration-500 hover:scale-[1.02] hover:shadow-[0_0_0_2px_rgba(2,6,23,0.45),0_12px_18px_-2px_rgba(2,6,23,1),0_30px_54px_rgba(2,6,23,1),0_75px_120px_rgba(2,6,23,0.9),0_120px_180px_rgba(2,6,23,0.7)] before:absolute before:-inset-2 before:rounded-[1.4rem] before:bg-cyan-400/30 before:blur-xl before:-z-10 sm:h-16 sm:w-16 sm:p-2.5 lg:h-32 lg:w-32 lg:rounded-3xl lg:p-3 lg:before:-inset-4 lg:before:rounded-[2rem] lg:before:bg-cyan-400/35 lg:before:blur-2xl"
          >
            <img
              src={logo}
              alt="Redruth Sea Angling Association Logo"
              className="h-full w-full object-contain scale-105"
            />
          </Link>
          <div className="min-w-0 flex flex-col items-start">
            <Link to="/" className="text-left">
              <p className="text-xs font-bold uppercase tracking-[0.22em] text-cyan-100 [text-shadow:0_1px_3px_rgba(2,6,23,0.6)] sm:text-sm sm:tracking-[0.3em]">Fish. Compete. Belong.</p>
              <p className="mt-1 text-sm font-bold leading-tight tracking-tight text-white [text-shadow:0_1px_3px_rgba(2,6,23,0.5)] sm:text-lg lg:text-base">Redruth Sea Angling Association</p>
            </Link>
            <div className="flex flex-wrap items-center gap-2 mt-1.5">
              <Link
                to="/membership"
                className="flex h-9 items-center justify-center rounded-md bg-cyan-400 px-4 py-2 text-sm font-bold text-slate-950 shadow-[0_12px_24px_-4px_rgba(0,0,0,0.8),0_6px_10px_-2px_rgba(0,0,0,0.6)] ring-1 ring-cyan-200/50 transition hover:scale-[1.02] hover:bg-cyan-300 hover:shadow-[0_16px_30px_-4px_rgba(0,0,0,0.85),0_8px_14px_-2px_rgba(0,0,0,0.65)]"
              >
                {tt("Join")}
              </Link>
              <SiteSearch />
            {/* Language dropdown — always visible */}
            <div className="relative">
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setOpenDropdown(openDropdown === 'lang' ? null : 'lang'); }}
                  className="inline-flex items-center gap-2 rounded-xl border border-cyan-400/30 bg-slate-800 px-3 py-2 text-sm font-semibold shadow-[0_14px_28px_-6px_rgba(0,0,0,0.8),0_6px_12px_-4px_rgba(0,0,0,0.6)] backdrop-blur transition hover:border-cyan-300/60 hover:bg-slate-700"
                >
                  {lang === 'en' ? (
                    <span className="text-base leading-none">🇬🇧</span>
                  ) : (
                    <div className="relative h-4 w-6 shrink-0 overflow-hidden rounded-sm">
                      <div className="h-full w-full bg-black">
                        <div className="absolute inset-x-0 top-1/2 h-[30%] -translate-y-1/2 bg-white" />
                        <div className="absolute inset-y-0 left-1/2 w-[22%] -translate-x-1/2 bg-white" />
                      </div>
                    </div>
                  )}
                  <span className="text-slate-200">{lang === 'en' ? 'English' : 'Kernowek'}</span>
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className={`h-4 w-4 text-slate-400 transition-transform duration-200 ${openDropdown === 'lang' ? 'rotate-180' : ''}`}><path fillRule="evenodd" d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06z" clipRule="evenodd" /></svg>
                </button>
                {openDropdown === 'lang' && (
                  <div className="absolute left-0 top-full z-50 mt-1 min-w-[140px] overflow-hidden rounded-xl border border-white/10 bg-slate-900/95 shadow-xl backdrop-blur-md">
                    <div className="h-0.5 w-full bg-gradient-to-r from-cyan-400 via-cyan-300 to-transparent" />
                    <button
                      type="button"
                      onClick={() => { setLang('en'); setOpenDropdown(null); }}
                      className={`flex w-full items-center gap-2.5 px-4 py-2.5 text-sm transition hover:bg-cyan-400/10 ${lang === 'en' ? 'text-cyan-300 font-semibold' : 'text-slate-300'}`}
                    >
                      <span className="text-base leading-none">🇬🇧</span>
                      English
                    </button>
                    <button
                      type="button"
                      onClick={() => { setLang('kw'); setOpenDropdown(null); }}
                      className={`flex w-full items-center gap-2.5 px-4 py-2.5 text-sm transition hover:bg-cyan-400/10 ${lang === 'kw' ? 'text-cyan-300 font-semibold' : 'text-slate-300'}`}
                    >
                      <div className="relative h-4 w-6 shrink-0 overflow-hidden rounded-sm">
                        <div className="h-full w-full bg-black">
                          <div className="absolute inset-x-0 top-1/2 h-[30%] -translate-y-1/2 bg-white" />
                          <div className="absolute inset-y-0 left-1/2 w-[22%] -translate-x-1/2 bg-white" />
                        </div>
                      </div>
                      Kernowek
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Tab strip — Home and Contact pinned, everything else scrolls sideways */}
        <div className="relative z-10 order-last flex w-full min-w-0 items-center gap-1.5 border-t border-white/10 pt-2 2xl:order-none 2xl:w-auto 2xl:flex-1 2xl:border-t-0 2xl:pl-2 2xl:pt-0">
          <Link
            to="/"
            onClick={() => setOpenDropdown(null)}
            className="shrink-0 whitespace-nowrap rounded-lg bg-cyan-400 px-3 py-2 text-sm font-bold text-slate-950 shadow-md transition hover:bg-cyan-300 2xl:text-xs"
          >
            {tt("Home")}
          </Link>
          <div className="relative min-w-0 flex-1">
            <nav
              ref={navScrollRef}
              onScroll={handleNavScroll}
              aria-label="Main"
              className="no-scrollbar flex snap-x items-center gap-1 overflow-x-auto overscroll-x-contain text-sm font-semibold text-white [text-shadow:0_1px_3px_rgba(15,23,42,0.55)] 2xl:text-xs"
            >
              {scrollNavItems.map((item) => item.children ? (
                <button
                  key={item.label}
                  type="button"
                  onClick={(e) => openNavDropdown(e, item.label)}
                  aria-expanded={openDropdown === item.label}
                  className={`flex shrink-0 snap-start items-center gap-0.5 whitespace-nowrap rounded-lg border px-3 py-2 transition duration-200 ${openDropdown === item.label ? 'border-cyan-300/60 bg-white/15 text-cyan-200' : 'border-white/10 bg-white/5 hover:bg-white/10 hover:text-cyan-200'}`}
                >
                  {tt(item.label)}
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className={`h-4 w-4 transition-transform duration-300 ${openDropdown === item.label ? "rotate-180" : ""}`}><path fillRule="evenodd" d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06z" clipRule="evenodd" /></svg>
                </button>
              ) : (
                <Link
                  key={item.label}
                  to={item.to}
                  onClick={() => setOpenDropdown(null)}
                  className="shrink-0 snap-start whitespace-nowrap rounded-lg border border-white/10 bg-white/5 px-3 py-2 transition duration-200 hover:bg-white/10 hover:text-cyan-200"
                >
                  {tt(item.label)}
                </Link>
              ))}
            </nav>
            {navCanScroll.left && (
              <button
                type="button"
                onClick={() => scrollNav(-1)}
                aria-label="Scroll menu left"
                className="absolute inset-y-0 left-0 flex w-9 items-center justify-start bg-gradient-to-r from-sky-950 via-sky-950/80 to-transparent text-cyan-200"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
            )}
            {navCanScroll.right && (
              <button
                type="button"
                onClick={() => scrollNav(1)}
                aria-label="Scroll menu right"
                className="absolute inset-y-0 right-0 flex w-9 items-center justify-end bg-gradient-to-l from-sky-950 via-sky-950/80 to-transparent text-cyan-200"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            )}
          </div>
          <Link
            to="/contact"
            onClick={() => setOpenDropdown(null)}
            className="shrink-0 whitespace-nowrap rounded-lg bg-cyan-400 px-3 py-2 text-sm font-bold text-slate-950 shadow-md transition hover:bg-cyan-300 2xl:text-xs"
          >
            {tt("Contact")}
          </Link>
        </div>
      </div>

      {/* Mobile home screen shortcut strip */}
      <HomeOnly>
      <div className="lg:hidden border-t border-cyan-400/20 bg-cyan-400/10 py-2 px-4 sm:px-6">
        <Link
          to="/install"
          className="inline-flex items-center gap-2 text-sm font-semibold text-cyan-100 transition hover:text-white"
        >
          📱 <span>{tt("Add RSAA to Your Home Screen")}</span>
          <span className="ml-1 rounded-full bg-cyan-400/20 px-2 py-0.5 text-xs font-bold text-cyan-100 animate-pulse">{tt("Tap here")}</span>
        </Link>
      </div>
      </HomeOnly>

      {openDropdown && (
        <div className="fixed inset-0 z-40" onClick={() => setOpenDropdown(null)} aria-hidden="true" />
      )}

      {openNavGroup && navDropdownPos && (
        <div
          className="fixed z-50 w-64 max-h-[70vh] overflow-y-auto rounded-2xl border border-white/10 bg-slate-900/95 shadow-2xl shadow-black/60 backdrop-blur-md"
          style={{ top: navDropdownPos.top, left: navDropdownPos.left }}
        >
          <div className="h-0.5 w-full bg-gradient-to-r from-cyan-400 via-cyan-300 to-transparent" />
          <div className="py-2">
          {openNavGroup.children.map((child) => child.href ? (
            <a
              key={child.href}
              href={child.href}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setOpenDropdown(null)}
              className="flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-slate-300 transition-all duration-150 hover:bg-cyan-400/10 hover:text-white hover:pl-5"
            >
              <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-cyan-400/60" />
              {tt(child.label)}
            </a>
          ) : (
            <Link
              key={child.to}
              to={child.to}
              onClick={() => setOpenDropdown(null)}
              className="flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-slate-300 transition-all duration-150 hover:bg-cyan-400/10 hover:text-white hover:pl-5"
            >
              <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-cyan-400/60" />
              {tt(child.label)}
            </Link>
          ))}
          </div>
        </div>
      )}
    </header>
);

const homeElement = (
            <div className="page-shell min-h-screen bg-slate-950 text-white">
              <PageMeta
                title="Redruth Sea Angling Association"
                description="Redruth Sea Angling Association – Cornwall's leading sea angling and sea fishing club since 1962. Shore and boat competitions, live sea webcams and coastal weather, what3words location guidance, angler safety advice, junior section, club records, fixtures and membership. Available in Kernowek (Cornish language). Serving Redruth, Falmouth, Truro and all of Cornwall."
                url="https://www.redruthseaanglingassociation.co.uk/"
                image="https://www.redruthseaanglingassociation.co.uk/assets/scenic_background.png"
              />
              <motion.section
                id="home"
                className="relative border-b border-white/10"
                style={{ minHeight: '90vh', minWidth: '100%' }}
              >
                {/* Background elements wrapped in overflow-hidden so they don't affect sticky */}
                <div className="absolute inset-0 overflow-hidden">
                  {/* Ken Burns animated background */}
                  <img
                    src="/assets/cornish_coastline.webp"
                    alt="Cornish coastline hero background"
                    className="hero-bg pointer-events-none absolute inset-0 h-full w-full object-cover"
                    style={{ objectPosition: 'center 35%', backgroundColor: '#0f172a' }}
                  />
                  {/* Cinematic overlay: dark left for text, opens up right to show the scene */}
                  <div className="absolute inset-0 bg-[linear-gradient(105deg,rgba(15,23,42,0.9)_0%,rgba(15,23,42,0.78)_55%,rgba(15,23,42,0.5)_100%)]" />
                  {/* Bottom fade into next section */}
                  <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-slate-950 to-transparent" />
                  {/* Cyan accent glow behind heading */}
                  <div className="pointer-events-none absolute left-0 top-1/3 h-[400px] w-[600px] -translate-y-1/2 bg-cyan-500/10 blur-[100px]" />
                </div>

<div className="relative z-10 mx-auto grid max-w-7xl gap-8 px-4 pb-12 pt-10 sm:px-6 sm:pb-16 sm:pt-12 lg:grid-cols-[1.2fr_0.8fr] lg:gap-12 lg:px-8 lg:pb-28 lg:pt-16">
                  <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.55 }}
                    className="max-w-3xl pt-2 sm:pt-4"
                  >
                    <div className="inline-flex max-w-full items-center gap-2 rounded-full border border-cyan-300/20 bg-cyan-400/10 px-3 py-2 text-xs text-cyan-100 panel-lift sm:px-4 sm:text-sm">
                      <Anchor className="h-4 w-4" />
                      {t('heroBadge')}
                    </div>

                    {/* Cornish flag + tagline + language toggle */}
                    <div className="mt-5 flex flex-wrap items-center gap-3">
                      <div className="relative h-7 w-11 shrink-0 overflow-hidden rounded-sm shadow-md" style={{ transformOrigin: 'left center' }}>
                        <style>{`
                          @keyframes flutter {
                            0%   { transform: perspective(200px) rotateY(0deg) skewY(0deg); }
                            25%  { transform: perspective(200px) rotateY(6deg) skewY(1deg); }
                            50%  { transform: perspective(200px) rotateY(0deg) skewY(0deg); }
                            75%  { transform: perspective(200px) rotateY(-4deg) skewY(-0.5deg); }
                            100% { transform: perspective(200px) rotateY(0deg) skewY(0deg); }
                          }
                          .flag-flutter { animation: flutter 2.5s ease-in-out infinite; }
                        `}</style>
                        <div className="flag-flutter h-full w-full bg-black">
                          {/* St Piran's cross — white cross on black */}
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="absolute inset-x-0 top-1/2 h-[30%] -translate-y-1/2 bg-white" />
                            <div className="absolute inset-y-0 left-1/2 w-[22%] -translate-x-1/2 bg-white" />
                          </div>
                        </div>
                      </div>
                      <div>
                        <p className="text-xs font-medium italic text-slate-100 [text-shadow:0_1px_4px_rgba(2,6,23,0.85)] sm:text-sm">An klowes gwithyas mor gwella yn Kernow</p>
                        <p className="text-xs text-slate-300 [text-shadow:0_1px_4px_rgba(2,6,23,0.85)]">The best sea fishing club in Cornwall</p>
                      </div>
                    </div>

                    <h1 className="mt-6 text-3xl font-bold tracking-tight text-white [text-shadow:0_3px_4px_rgba(2,6,23,1),0_12px_20px_rgba(2,6,23,0.95),0_35px_55px_rgba(2,6,23,0.85),0_65px_110px_rgba(2,6,23,0.65),0_100px_160px_rgba(2,6,23,0.45)] sm:text-5xl lg:text-6xl xl:text-7xl">
                      Redruth Sea Angling Association
                    </h1>

                    <p className="mt-5 max-w-2xl text-base leading-7 text-slate-200 [text-shadow:0_1px_6px_rgba(2,6,23,0.7)] sm:text-lg sm:leading-8 lg:text-xl">
                      {t('heroDesc')}
                    </p>
                    <p className="mt-3 text-xs font-medium tracking-wide text-slate-300 [text-shadow:0_1px_4px_rgba(2,6,23,0.8)] sm:text-sm">
                      {t('heroAffiliated')}
                    </p>
                    



                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 28 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.1 }}
                    className="flex items-stretch lg:pl-2 mt-8 sm:mt-0"
                  >
                    <div className="w-full rounded-[2rem] border border-white/10 bg-gradient-to-br from-indigo-950 via-blue-900 to-cyan-600 p-5 shadow-[0_4px_8px_rgba(2,6,23,0.8),0_20px_35px_rgba(2,6,23,0.7),0_55px_90px_rgba(2,6,23,0.55),0_90px_140px_rgba(2,6,23,0.35)] backdrop-blur sm:p-6 lg:p-7">
                      <div className="flex items-start justify-between gap-4 mb-6">
                        <div>
                          <p className="text-xs uppercase tracking-[0.3em] text-cyan-200">{tt("Why join our club?")}</p>
                          <h2 className="heading-glow mt-1.5 text-xl font-bold sm:text-2xl">{tt("Why Join RSAA?")}</h2>
                        </div>
                        <div className="rounded-2xl bg-violet-400/10 p-2.5 ring-1 ring-violet-400/20">
                          <Sparkles className="h-5 w-5 text-violet-300" />
                        </div>
                      </div>

                      <div className="flex flex-col gap-3">
                        {[
                          { icon: Fish,        title: "Competitions & Fishing",  desc: "Regular shore and boat competitions, club records, trophies and leaderboard tracking.",  iconBg: "bg-blue-500/15 ring-blue-400/30",   iconColor: "text-blue-400" },
                          { icon: Users,       title: "Welcoming Community",     desc: "A friendly Cornish angling community with strong support for beginners and new members.",  iconBg: "bg-violet-500/15 ring-violet-400/30", iconColor: "text-violet-400" },
                          { icon: Leaf,        title: "Conservation Ethos",      desc: "Catch-and-release focused, promoting sustainable and responsible sea angling.",             iconBg: "bg-green-500/15 ring-green-400/30",   iconColor: "text-green-400" },
                          { icon: PartyPopper, title: "Social & Club Life",      desc: "Social evenings, club gatherings, guest speakers, and junior anglers actively encouraged.", iconBg: "bg-amber-500/15 ring-amber-400/30",   iconColor: "text-amber-400" },
                        ].map(({ icon: Icon, title, desc, iconBg, iconColor }) => (
                          <div key={title} className="flex items-start gap-4 rounded-2xl border border-white/8 bg-white/5 px-4 py-3.5 shadow-lg shadow-black/20 transition hover:border-cyan-400/40 hover:bg-cyan-400/5 hover:shadow-xl hover:shadow-cyan-950/30">
                            <div className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-xl ring-1 ${iconBg}`}>
                              <Icon className={`h-4 w-4 ${iconColor}`} />
                            </div>
                            <div>
                              <p className="text-sm font-semibold text-white">{tt(title)}</p>
                              <p className="mt-0.5 text-xs leading-5 text-slate-200">{tt(desc)}</p>
                            </div>
                          </div>
                        ))}
                      </div>

                      <div className="mt-6 flex flex-col items-center gap-3">
                        <div className="w-full rounded-2xl border border-cyan-400/30 bg-cyan-400/10 px-4 py-3 text-center panel-lift">
                          <p className="text-sm font-bold text-cyan-300">{tt("Become a Member")}</p>
                          <p className="text-xs text-cyan-100 mt-0.5">Adult <span className="font-bold text-white">£15</span> · Junior <span className="font-bold text-white">£1</span> per year</p>
                        </div>
                        <Link
                          to="/membership"
                          className="w-full rounded-2xl bg-gradient-to-r from-cyan-400 to-emerald-400 py-3.5 text-center text-base font-bold text-slate-950 shadow-lg shadow-cyan-500/30 transition hover:scale-[1.03] hover:shadow-cyan-400/40 active:scale-[0.98]"
                        >
                          {tt("Join RSAA Today →")}
                        </Link>
                      </div>
                    </div>
                  </motion.div>
                </div>
              </motion.section>

              <main>

                <NextCompetitionBanner lang={lang} />

                <FishRecorderBanner lang={lang} />

                <RayBassHuntTeaser />

                <Notifications />

                <motion.section initial={{ opacity: 0, y: 32 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-100px" }} transition={{ duration: 0.6, ease: "easeOut" }} id="about" className="bg-gradient-to-b from-slate-950 to-slate-900 px-4 py-16 sm:px-6 lg:px-8">
                  <div className="mx-auto max-w-4xl">
                    <p className="text-sm font-semibold uppercase tracking-[0.3em] text-cyan-300">{t('aboutLabel')}</p>
                    <h2 className="heading-glow mt-4 text-3xl font-bold tracking-tight sm:text-4xl">{t('aboutHeading')}</h2>
                  </div>

                  <div className="mx-auto mt-10 max-w-4xl space-y-6">
                    {[
                      { icon: Users,       heading: t('card1Heading'), paras: [t('card1Para1'), t('card1Para2')] },
                      { icon: Fish,        heading: t('card2Heading'), paras: [t('card2Para1'), t('card2Para2'), t('card2Para3')] },
                      { icon: CalendarDays,heading: t('card3Heading'), paras: [t('card3Para1'), t('card3Para2')] },
                      { icon: ShieldCheck, heading: t('card4Heading'), paras: [t('card4Para1'), t('card4Para2')] },
                    ].map(({ icon: Icon, heading, paras }) => (
                      <div key={heading} className="rounded-[2rem] border border-white/10 bg-gradient-to-br from-white/5 to-white/[0.02] p-8 shadow-2xl shadow-black/40 sm:p-10">
                        <div className="flex items-center gap-4">
                          <div className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-cyan-400/10 to-cyan-400/5 border border-cyan-300/20">
                            <Icon className="h-6 w-6 text-cyan-300" />
                          </div>
                          <h3 className="text-xl font-bold text-white sm:text-2xl">{heading}</h3>
                        </div>
                        <div className="mt-5 space-y-3">
                          {paras.map((p, i) => (
                            <p key={i} className="text-base leading-8 text-slate-300">{p}</p>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </motion.section>

                <CatchesSlideshow lang={lang} />

                <FishOfMonthTeaser />

                <motion.section initial={{ opacity: 0, y: 32 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-100px" }} transition={{ duration: 0.6, ease: "easeOut" }} id="membership" className="relative border-y border-white/10 overflow-hidden">
                  <LazyBackgroundVideo
                    src="/assets/jumping_tuna_web.mp4"
                    poster="/assets/jumping_tuna_poster.webp"
                    className="pointer-events-none absolute inset-0 h-full w-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-slate-950/90 via-slate-950/65 to-slate-950/35" />
                  <div className="relative mx-auto grid max-w-7xl gap-8 px-4 py-16 sm:px-6 sm:py-20 lg:grid-cols-[0.9fr_1.1fr] lg:px-8">
                    <div>
                      <p className="text-sm font-semibold uppercase tracking-[0.3em] text-cyan-300">{tt("Membership")}</p>
                      <h2 className="mt-4 text-3xl font-bold tracking-tight sm:text-4xl">{tt("How to join the Redruth Sea Angling Association")}</h2>
                    </div>

                    <div className="grid gap-4 md:grid-cols-2">
                      <div className="rounded-[2rem] border border-cyan-300/20 bg-cyan-400/10 p-6 panel-lift">
                        <p className="text-sm uppercase tracking-[0.25em] text-cyan-200/80">{tt("Adult annual subscription")}</p>
                        <p className="mt-3 text-5xl font-bold text-white">£15</p>
                        <p className="mt-4 text-sm leading-7 text-slate-200">
                          {tt("Includes club membership, liability cover, and support for general administration.")}
                        </p>
                      </div>

                      <div className="rounded-[2rem] border border-amber-300/20 bg-amber-400/10 p-6 panel-lift">
                        <p className="text-sm uppercase tracking-[0.25em] text-amber-200/80">{tt("Junior annual subscription")}</p>
                        <p className="mt-3 text-5xl font-bold text-white">£1</p>
                        <p className="mt-4 text-sm leading-7 text-slate-200">
                          {tt("Open to junior members. A parent or guardian must co-sign the membership application.")}
                        </p>
                      </div>

                      <div className="rounded-[2rem] border border-white/10 bg-white/5 p-6 md:col-span-2 panel-lift">
                        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                          <div>
                            <h3 className="text-xl font-semibold">{tt("Ready to join Redruth Sea Angling Association?")}</h3>
                            <p className="mt-2 text-sm leading-7 text-slate-300">
                              {tt("Adult membership is £15 · Junior membership is £1. Complete the form below and pay via card/bank via SumUp. Alternatively, new and prospective members are always welcome to come along to one of our meetings and pay in person.")}
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => scrollToSection("paypal")}
                            className="rounded-2xl bg-cyan-400 px-5 py-3 text-sm font-semibold text-slate-950 transition hover:scale-[1.02]"
                          >
                            {tt("Start membership")}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.section>

                <motion.section initial={{ opacity: 0, y: 32 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-100px" }} transition={{ duration: 0.6, ease: "easeOut" }} id="fixtures" className="relative overflow-hidden px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
                  <img src="/assets/Back_ground_1.webp" alt="" aria-hidden="true" className="section-bg pointer-events-none absolute inset-0 h-full w-full object-cover" style={{ objectPosition: 'center 45%' }} />
                  <div className="absolute inset-0 bg-gradient-to-b from-slate-950/85 via-slate-950/70 to-slate-950/85" />
                  <div className="relative mx-auto max-w-7xl">
                  <div className="max-w-3xl">
                    <p className="text-sm font-semibold uppercase tracking-[0.3em] text-cyan-300">{tt("Fixtures")}</p>
                    <div className="mt-4 flex items-center gap-4">
                      <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">{tt("Upcoming fixtures calendar")}</h2>
                      <button type="button" onClick={() => setFixturesOpen((o) => !o)} className="flex items-center gap-2 rounded-xl border border-cyan-300/40 bg-cyan-400/15 px-4 py-2 text-sm font-semibold text-cyan-300 transition hover:bg-cyan-400/25 shrink-0">
                        {fixturesOpen ? tt("Hide") : tt("Show")}
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className={`h-6 w-6 transition-transform duration-300 ${fixturesOpen ? "rotate-180" : ""}`}><path fillRule="evenodd" d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06z" clipRule="evenodd" /></svg>
                      </button>
                    </div>
                    <div className="mt-6">
                      <Link to="/scales-policy" className="inline-flex items-center gap-3 rounded-xl border border-cyan-300/40 bg-cyan-500/20 px-5 py-3 text-sm font-semibold text-cyan-100 shadow-lg shadow-cyan-950/30 transition hover:bg-cyan-500/30 hover:border-cyan-300/60">
                        <Scale className="h-5 w-5 text-cyan-300" />
                        <span>{tt("Please read: Weighing Scales Policy Notice")} <span className="ml-1 text-cyan-300">→</span></span>
                      </Link>
                    </div>
                  </div>

                  <RiskAssessmentTabs lang={lang} />

                  <div className="mt-6">
                    <NextCompetitionBanner lang={lang} />
                  </div>

                  <AnimatePresence initial={false}>
                  {fixturesOpen && (
                  <motion.div
                    key="fixtures-panel"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.35, ease: "easeInOut" }}
                    className="overflow-hidden"
                  >
                  <div className="mt-8 grid gap-5 md:grid-cols-2 xl:grid-cols-4 sm:mt-10">
                    {fixtureCards.map((fixture) => (
                      <div key={`${fixture.date}-${fixture.type}`} className="flex flex-col rounded-[2rem] border border-white/10 bg-white/5 p-6 shadow-xl shadow-cyan-950/20 backdrop-blur">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="inline-block self-start rounded-md bg-sky-400/20 px-2.5 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-sky-300">{fixture.date}</p>
                          {fixture.monthLong && (
                            <p className="inline-block self-start rounded-md border border-fuchsia-400/40 bg-fuchsia-400/10 px-2.5 py-1 text-xs font-bold uppercase tracking-[0.2em] text-fuchsia-300">{tt("Month Long Competition")}</p>
                          )}
                        </div>
                        <h3 className="mt-3 text-lg font-semibold text-white sm:text-2xl">{tt(fixture.type)}</h3>
                        <p className="mt-3 text-sm font-medium text-slate-200">{fixture.venue}</p>
                        <p className="mt-4 text-sm leading-6 text-slate-300">{fixture.time}</p>
                        {(fixture.rules || fixture.venueType) && (
                          <div className="mt-5 flex flex-wrap gap-4">
                            {fixture.rules && (
                              <a
                                href={fixture.rules}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1.5 text-xs font-semibold text-cyan-300 hover:text-cyan-100 transition"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-3.5 w-3.5 shrink-0">
                                  <path fillRule="evenodd" d="M4.5 2A1.5 1.5 0 003 3.5v13A1.5 1.5 0 004.5 18h11a1.5 1.5 0 001.5-1.5V7.621a1.5 1.5 0 00-.44-1.06l-4.12-4.122A1.5 1.5 0 0011.378 2H4.5zm4.75 6.75a.75.75 0 011.5 0v2.546l.943-1.048a.75.75 0 111.114 1.004l-2.25 2.5a.75.75 0 01-1.114 0l-2.25-2.5a.75.75 0 111.114-1.004l.943 1.048V8.75z" clipRule="evenodd" />
                                </svg>
                                {tt("Competition Rules")}
                              </a>
                            )}
                            {fixture.venueType && (
                              <a
                                href={riskAssessments[fixture.venueType].file}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-400 hover:text-amber-200 transition"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-3.5 w-3.5 shrink-0">
                                  <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                                </svg>
                                {tt(riskAssessments[fixture.venueType].label)}
                              </a>
                            )}
                          </div>
                        )}
                        <div className="mt-auto pt-5">
                          <button
                            type="button"
                            onClick={() => {
                              const text = [fixture.type, fixture.date, fixture.venue, fixture.time].filter(Boolean).join('\n');
                              if (navigator.share) {
                                navigator.share({ title: `RSAA – ${fixture.type}`, text, url: window.location.origin });
                              } else {
                                navigator.clipboard?.writeText(`${fixture.type}\n${fixture.date}\n${text}\n${window.location.origin}`);
                              }
                            }}
                            className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-400 transition hover:text-slate-200"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4 shrink-0">
                              <path d="M13 4.5a2.5 2.5 0 1 1 .702 1.737L6.97 9.604a2.518 2.518 0 0 1 0 .792l6.733 3.367a2.5 2.5 0 1 1-.671 1.341l-6.733-3.367a2.5 2.5 0 1 1 0-3.474l6.733-3.366A2.52 2.52 0 0 1 13 4.5z" />
                            </svg>
                            {tt("Share")}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                  </motion.div>
                  )}
                  </AnimatePresence>
                  </div>
                </motion.section>

                <FishingConditionsTeaser />

                <motion.section initial={{ opacity: 0, y: 32 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-100px" }} transition={{ duration: 0.6, ease: "easeOut" }} id="records" className="relative border-y border-white/10 overflow-hidden">
                  <img src="/assets/scenic_background.webp" alt="" aria-hidden="true" className="section-bg pointer-events-none absolute inset-0 h-full w-full object-cover" style={{ objectPosition: 'center 40%' }} />
                  <div className="absolute inset-0 bg-gradient-to-b from-slate-950/85 via-slate-950/65 to-slate-950/85" />
                  <div className="relative mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
                    <div className="max-w-3xl">
                      <p className="text-sm font-semibold uppercase tracking-[0.3em] text-cyan-300">{tt("Records and trophies")}</p>
                      <h2 className="mt-4 text-3xl font-bold tracking-tight sm:text-4xl">{tt("Club Records & Annual Trophies")}</h2>
                    </div>

                    <div className="mt-8 grid gap-5 sm:grid-cols-2 xl:grid-cols-5 sm:mt-10">
                      <Link
                        to="/records"
                        className="group flex flex-col rounded-[2rem] border border-cyan-300/20 bg-cyan-400/5 p-7 transition hover:border-cyan-300/40 hover:bg-cyan-400/10 panel-lift"
                      >
                        <Trophy className="h-7 w-7 text-cyan-300" />
                        <h3 className="mt-5 text-xl font-semibold text-white">{tt("Catch & Release Records")}</h3>
                        <p className="mt-3 text-sm leading-7 text-slate-300">{tt("Shore and boat catch & release records for all species.")}</p>
                        <span className="mt-5 text-sm font-medium text-cyan-300 group-hover:underline">{tt("View records →")}</span>
                      </Link>

                      <Link
                        to="/rsaa-records"
                        className="group flex flex-col rounded-[2rem] border border-emerald-300/20 bg-emerald-400/5 p-7 transition hover:border-emerald-300/40 hover:bg-emerald-400/10 panel-lift"
                      >
                        <Trophy className="h-7 w-7 text-emerald-300" />
                        <h3 className="mt-5 text-xl font-semibold text-white">{tt("Official Club Records")}</h3>
                        <p className="mt-3 text-sm leading-7 text-slate-300">{tt("Historic shore and boat official records dating back to the 1960s.")}</p>
                        <span className="mt-5 text-sm font-medium text-emerald-300 group-hover:underline">{tt("View records →")}</span>
                      </Link>

                      <Link
                        to="/junior-records"
                        className="group flex flex-col rounded-[2rem] border border-amber-300/20 bg-amber-400/5 p-7 transition hover:border-amber-300/40 hover:bg-amber-400/10 panel-lift"
                      >
                        <Trophy className="h-7 w-7 text-amber-300" />
                        <h3 className="mt-5 text-xl font-semibold text-white">{tt("Junior Records")}</h3>
                        <p className="mt-3 text-sm leading-7 text-slate-300">{tt("Official shore and boat records for junior members.")}</p>
                        <span className="mt-5 text-sm font-medium text-amber-300 group-hover:underline">{tt("View records →")}</span>
                      </Link>

                      <Link
                        to="/junior-catch-release"
                        className="group flex flex-col rounded-[2rem] border border-rose-300/20 bg-rose-400/5 p-7 transition hover:border-rose-300/40 hover:bg-rose-400/10"
                      >
                        <Trophy className="h-7 w-7 text-rose-300" />
                        <h3 className="mt-5 text-xl font-semibold text-white">{tt("Junior C&R Records")}</h3>
                        <p className="mt-3 text-sm leading-7 text-slate-300">{tt("Shore and boat catch & release records for junior members.")}</p>
                        <span className="mt-5 text-sm font-medium text-rose-300 group-hover:underline">{tt("View records →")}</span>
                      </Link>

                      <div className="flex flex-col rounded-[2rem] border border-white/10 bg-white/5 p-7 panel-lift">
                        <Trophy className="h-7 w-7 text-cyan-300" />
                        <h3 className="mt-5 text-xl font-semibold text-white">{tt("Annual Trophies")}</h3>
                        <p className="mt-3 text-sm leading-7 text-slate-300">{trophyCategories.reduce((n, c) => n + c.trophies.length, 0)} trophies across {trophyCategories.length} categories including shore, boat, junior, ladies, and champion angler awards.</p>
                        <button
                          type="button"
                          onClick={() => {
                            const el = document.getElementById("trophies");
                            if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY, behavior: "smooth" });
                          }}
                          className="mt-5 text-left text-sm font-medium text-cyan-300 hover:underline"
                        >
                          {tt("See all trophies ↓")}
                        </button>
                      </div>
                    </div>

                    {/* CFSA Calculator featured banner */}
                    <div className="mt-5 grid gap-4 sm:grid-cols-2">
                      <Link
                        to="/cfsa-specimen"
                        className="group flex flex-col sm:flex-row items-center gap-5 overflow-hidden rounded-[2rem] border border-violet-400/30 bg-gradient-to-r from-violet-600/20 via-violet-500/10 to-cyan-600/10 p-6 ring-1 ring-violet-400/10 transition hover:border-violet-400/50 hover:from-violet-600/30 hover:to-cyan-600/20 panel-lift"
                      >
                        <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-violet-400/20 ring-1 ring-violet-400/20">
                          <Calculator className="h-7 w-7 text-violet-300" />
                        </div>
                        <div className="flex-1 text-center sm:text-left">
                          <p className="text-xs font-semibold uppercase tracking-widest text-violet-400">{tt("Tool")}</p>
                          <h3 className="mt-1 text-lg font-bold text-white">{tt("CFSA Specimen Calculator")}</h3>
                          <p className="mt-1 text-sm leading-6 text-slate-300">{tt("Check if your catch qualifies as a CFSA Specimen with a percentage score.")}</p>
                        </div>
                        <span className="shrink-0 rounded-xl bg-violet-500 px-4 py-2 text-sm font-bold text-white shadow-lg shadow-violet-900/40 transition group-hover:bg-violet-400">
                          {tt("Open →")}
                        </span>
                      </Link>
                      <Link
                        to="/competition-calculator"
                        className="group flex flex-col sm:flex-row items-center gap-5 overflow-hidden rounded-[2rem] border border-cyan-400/30 bg-gradient-to-r from-cyan-600/20 via-cyan-500/10 to-violet-600/10 p-6 ring-1 ring-cyan-400/10 transition hover:border-cyan-400/50 hover:from-cyan-600/30 hover:to-violet-600/20 panel-lift"
                      >
                        <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-cyan-400/20 ring-1 ring-cyan-400/20">
                          <Trophy className="h-7 w-7 text-cyan-300" />
                        </div>
                        <div className="flex-1 text-center sm:text-left">
                          <p className="text-xs font-semibold uppercase tracking-widest text-cyan-400">{tt("Tool")}</p>
                          <h3 className="mt-1 text-lg font-bold text-white">{tt("Competitions Leaderboard Calculator")}</h3>
                          <p className="mt-1 text-sm leading-6 text-slate-300">{tt("Calculate competition standings, best N fish, and heaviest bag scores.")}</p>
                        </div>
                        <span className="shrink-0 rounded-xl bg-cyan-500 px-4 py-2 text-sm font-bold text-white shadow-lg shadow-cyan-900/40 transition group-hover:bg-cyan-400">
                          {tt("Open →")}
                        </span>
                      </Link>
                    </div>

                    <div id="trophies" className="mt-5 rounded-[2rem] border border-white/10 bg-white/5 p-7 panel-lift">
                      <h3 className="text-xl font-semibold text-white">{tt("Annual Trophies")}</h3>
                      <div className="mt-4 divide-y divide-white/10">
                        {trophyCategories.map((cat) => (
                          <details key={cat.label} className="group py-3">
                            <summary className="flex cursor-pointer list-none items-center justify-between text-sm font-semibold text-slate-200 [&::-webkit-details-marker]:hidden">
                              {tt(cat.label)}
                              <ChevronRight className="h-4 w-4 flex-shrink-0 text-cyan-300 transition-transform group-open:rotate-90" />
                            </summary>
                            <ul className="mt-3 space-y-1.5 pl-2">
                              {cat.trophies.map((t) => (
                                <li key={t.name} className="text-sm text-slate-300">
                                  <span className="font-medium text-white">{t.name}</span> – {t.award}
                                </li>
                              ))}
                            </ul>
                          </details>
                        ))}
                      </div>
                    </div>
                  </div>
                </motion.section>

                <motion.section initial={{ opacity: 0, y: 32 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-100px" }} transition={{ duration: 0.6, ease: "easeOut" }} id="contact" className="relative border-t border-white/10 overflow-hidden">
                  <img src="/assets/background_1.webp" alt="" aria-hidden="true" className="section-bg pointer-events-none absolute inset-0 h-full w-full object-cover" style={{ objectPosition: 'center 35%' }} />
                  <div className="absolute inset-0 bg-gradient-to-b from-slate-950/90 via-slate-950/75 to-slate-950/90" />
                  <div className="relative mx-auto grid max-w-7xl gap-8 px-4 py-16 sm:px-6 sm:py-20 lg:grid-cols-[0.9fr_1.1fr] lg:px-8">
                    <div>
                      <p className="text-sm font-semibold uppercase tracking-[0.3em] text-cyan-300">{tt("Contact")}</p>
                      <h2 className="mt-4 text-3xl font-bold tracking-tight sm:text-4xl">{tt("Get in touch")}</h2>
                      <p className="mt-5 text-base leading-7 text-slate-300 sm:mt-6 sm:text-lg sm:leading-8">
                        {tt("Get in touch with us using the form, by phone, or come along to one of our monthly meetings.")}
                      </p>

                      <div className="mt-8 space-y-4">
                        <div className="flex items-start gap-4 rounded-[1.6rem] border border-white/10 bg-white/5 p-5 panel-lift">
                          <MapPin className="mt-1 h-5 w-5 text-cyan-300" />
                          <div>
                            <p className="font-semibold text-white">{tt("Meeting venue")}</p>
                            <p className="mt-1 text-sm leading-7 text-slate-300">St Rumons Social Club, Penryn Street, Redruth TR15 2SP</p>
                            <a
                              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent("St Rumons Social Club, Penryn Street, Redruth TR15 2SP")}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-cyan-300 transition hover:text-cyan-200"
                            >
                              {tt("See on map")} <ExternalLink className="h-3 w-3" />
                            </a>
                            <p className="mt-2 text-xs text-slate-400">2026 dates (8pm): 2nd Mar · 13th Apr · 11th May · 1st Jun · 6th Jul · 3rd Aug · 7th Sep · 5th Oct · 2nd Nov · 7th Dec · AGM Jan 2027</p>
                            <p className="mt-1 text-xs text-slate-500">{tt("Some meetings may be rescheduled — members notified by email and WhatsApp")}</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-4 rounded-[1.6rem] border border-white/10 bg-white/5 p-5 panel-lift">
                          <Phone className="mt-1 h-5 w-5 text-cyan-300" />
                          <div>
                            <p className="font-semibold text-white">{tt("Telephone")}</p>
                            <p className="text-xs text-slate-400">{tt("Honorary Secretary")} — Tim Clotworthy</p>
                            <p className="mt-1 text-sm leading-7 text-slate-300">07766 826693</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-4 rounded-[1.6rem] border border-white/10 bg-white/5 p-5 panel-lift">
                          <Mail className="mt-1 h-5 w-5 text-cyan-300" />
                          <div>
                            <p className="font-semibold text-white">{tt("Email")}</p>
                            <p className="mt-1 text-sm leading-7 text-slate-300">tim.clotworthy@btinternet.com</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="rounded-[2rem] border border-white/10 bg-white/5 p-7 panel-lift">
                      <h3 className="text-2xl font-semibold text-white">{tt("Send an enquiry")}</h3>
                      {contactSent ? (
                        <div className="mt-6 rounded-2xl border border-cyan-300/30 bg-cyan-400/10 px-5 py-4 panel-lift">
                          <p className="text-sm text-cyan-100">{tt("Message sent — we'll be in touch soon.")}</p>
                          <button
                            type="button"
                            onClick={() => setContactSent(false)}
                            className="mt-3 text-xs font-medium text-cyan-300 underline hover:text-cyan-200"
                          >
                            {tt("Send another message")}
                          </button>
                        </div>
                      ) : (
                        <form
                          name="contact"
                          method="POST"
                          data-netlify="true"
                          onSubmit={handleSendEnquiry}
                          className="mt-6 grid gap-4"
                        >
                          <input type="hidden" name="form-name" value="contact" />
                          <input
                            type="text"
                            name="name"
                            placeholder={tt("Your name")}
                            required
                            value={contactName}
                            onChange={(e) => setContactName(e.target.value)}
                            className="rounded-2xl border border-white/10 bg-slate-950/70 px-4 py-3 text-white placeholder:text-slate-500 outline-none focus:border-cyan-300/40"
                          />
                          <input
                            type="email"
                            name="email"
                            placeholder={tt("Your email")}
                            required
                            value={contactEmail}
                            onChange={(e) => setContactEmail(e.target.value)}
                            className="rounded-2xl border border-white/10 bg-slate-950/70 px-4 py-3 text-white placeholder:text-slate-500 outline-none focus:border-cyan-300/40"
                          />
                          <textarea
                            name="message"
                            rows={6}
                            placeholder={tt("Your message")}
                            required
                            value={contactMessage}
                            onChange={(e) => setContactMessage(e.target.value)}
                            className="rounded-2xl border border-white/10 bg-slate-950/70 px-4 py-3 text-white placeholder:text-slate-500 outline-none focus:border-cyan-300/40"
                          />
                          {contactError && (
                            <p className="rounded-2xl border border-red-400/30 bg-red-400/10 px-4 py-3 text-sm text-red-300">
                              {tt("Sorry, there was a problem sending your message. Please email")} <a href="mailto:tim.clotworthy@btinternet.com" className="underline hover:text-red-200">tim.clotworthy@btinternet.com</a> {tt("directly.")}
                            </p>
                          )}
                          <button
                            type="submit"
                            className="w-fit rounded-2xl bg-cyan-400 px-5 py-3 font-semibold text-slate-950 transition hover:scale-[1.02]"
                          >
                            {tt("Send message")}
                          </button>
                        </form>
                      )}
                    </div>

                    <div id="paypal" className="mt-6 lg:col-span-2">
                      <div className="rounded-[2rem] border border-cyan-300/20 bg-slate-950/70 p-7 panel-lift">
                        <h3 className="text-xl font-semibold text-white">{tt("Membership Application")}</h3>
                        <p className="mt-2 text-sm text-slate-300">{tt("Please complete all details below, then proceed to payment.")}</p>

                        {/* Tabs */}
                        <div className="mt-5 flex gap-2">
                          <button
                            type="button"
                            onClick={() => setMembershipTab("adult")}
                            className={`rounded-xl px-5 py-2.5 text-sm font-semibold transition ${membershipTab === "adult" ? "bg-cyan-400 text-slate-950" : "border border-white/10 bg-white/5 text-slate-300 hover:bg-white/10"}`}
                          >
                            {tt("Adult — £15")}
                          </button>
                          <button
                            type="button"
                            onClick={() => setMembershipTab("junior")}
                            className={`rounded-xl px-5 py-2.5 text-sm font-semibold transition ${membershipTab === "junior" ? "bg-amber-400 text-slate-950" : "border border-white/10 bg-white/5 text-slate-300 hover:bg-white/10"}`}
                          >
                            {tt("Junior — £1")}
                          </button>
                        </div>

                        {/* Adult form */}
                        {membershipTab === "adult" && (
                          <>
                            <div className="mt-6 grid gap-4 sm:grid-cols-2">
                              <input
                                type="text"
                                placeholder={tt("Full Name")}
                                value={memberName}
                                onChange={(e) => setMemberName(e.target.value)}
                                className="rounded-2xl border border-white/10 bg-slate-900/80 px-4 py-3 text-white placeholder:text-slate-500 outline-none focus:border-cyan-300/40"
                              />
                              <input
                                type="tel"
                                placeholder={tt("Mobile Number")}
                                value={memberMobile}
                                onChange={(e) => setMemberMobile(e.target.value)}
                                className="rounded-2xl border border-white/10 bg-slate-900/80 px-4 py-3 text-white placeholder:text-slate-500 outline-none focus:border-cyan-300/40"
                              />
                              <textarea
                                rows={3}
                                placeholder={tt("Full Address including Postcode")}
                                value={memberAddress}
                                onChange={(e) => setMemberAddress(e.target.value)}
                                className="sm:col-span-2 rounded-2xl border border-white/10 bg-slate-900/80 px-4 py-3 text-white placeholder:text-slate-500 outline-none focus:border-cyan-300/40"
                              />
                              <input
                                type="email"
                                placeholder={tt("Email Address")}
                                value={memberEmail}
                                onChange={(e) => setMemberEmail(e.target.value)}
                                className="sm:col-span-2 rounded-2xl border border-white/10 bg-slate-900/80 px-4 py-3 text-white placeholder:text-slate-500 outline-none focus:border-cyan-300/40"
                              />
                            </div>
                            <label className="mt-5 flex items-start gap-3 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={rulesAgreed}
                                onChange={(e) => setRulesAgreed(e.target.checked)}
                                className="mt-1 h-4 w-4 shrink-0 accent-cyan-400"
                              />
                              <span className="text-sm leading-6 text-slate-300">
                                {tt("I have read and agree to the")}{" "}
                                <a href="/assets/rsaa_club_rules.pdf" target="_blank" rel="noopener noreferrer" className="text-cyan-300 underline hover:text-cyan-200">{tt("Club Rules")}</a>
                                {" "}{tt("and the")}{" "}
                                <a href="/assets/rsaa_constitution.pdf" target="_blank" rel="noopener noreferrer" className="text-cyan-300 underline hover:text-cyan-200">{tt("Club Constitution")}</a>
                              </span>
                            </label>
                            {!membershipReady && (
                              <p className="mt-3 rounded-xl border border-amber-300/20 bg-amber-400/10 px-4 py-2.5 text-sm text-amber-300">{tt("Complete all fields and agree to the Club Rules to unlock payment.")}</p>
                            )}
                            <div className="mt-5 flex flex-col gap-3 sm:flex-row">
                              {/* <a
                                href={membershipReady ? "https://www.paypal.com/cgi-bin/webscr?cmd=_xclick&business=redruthseaanglingassociation@gmail.com&amount=15.00&currency_code=GBP&item_name=RSAA%20Annual%20Membership" : undefined}
                                onClick={handleMembershipClick}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`flex w-full sm:w-auto items-center justify-center gap-3 rounded-2xl px-8 py-4 text-base font-semibold shadow-xl transition ${
                                  membershipReady
                                    ? "bg-gradient-to-r from-cyan-400 to-emerald-400 text-slate-950 shadow-cyan-500/30 hover:scale-[1.05] hover:shadow-cyan-400/40 active:scale-[0.97]"
                                    : "cursor-not-allowed bg-slate-700 text-slate-400 shadow-none"
                                }`}
                              >
                                <CreditCard className="h-5 w-5" />
                                Pay £15 via PayPal
                              </a> */}
                              <a
                                href={membershipReady ? `https://pay.sumup.com/b2c/QUNL1J1W?customer-name=${encodeURIComponent(memberName)}` : undefined}
                                onClick={handleMembershipClick}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`flex w-full sm:w-auto items-center justify-center gap-3 rounded-2xl px-8 py-4 text-base font-semibold shadow-xl transition ${
                                  membershipReady
                                    ? "bg-gradient-to-r from-slate-100 to-white text-slate-950 shadow-slate-400/20 hover:scale-[1.05] hover:shadow-slate-300/30 active:scale-[0.97]"
                                    : "cursor-not-allowed bg-slate-700 text-slate-400 shadow-none"
                                }`}
                              >
                                <CreditCard className="h-5 w-5" />
                                {tt("Pay £15 via Card / Bank")}
                              </a>
                            </div>
                            {membershipReady && (
                              <div className="mt-4 rounded-2xl border border-white/10 bg-white/5 px-5 py-4 panel-lift">
                                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-cyan-300">{tt("Pay by Bank Transfer (BACS)")}</p>
                                <p className="mt-1 text-xs text-slate-400">{tt("Please use your name as the payment reference.")}</p>
                                <div className="mt-3 grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
                                  <span className="text-slate-400">{tt("Account name")}</span>
                                  <span className="font-medium text-white">Redruth Sea Angling Association</span>
                                  <span className="text-slate-400">{tt("Sort code")}</span>
                                  <span className="font-medium text-white">30-97-00</span>
                                  <span className="text-slate-400">{tt("Account number")}</span>
                                  <span className="font-medium text-white">00184090</span>
                                </div>
                                <div className="mt-4 border-t border-white/10 pt-4">
                                  {bacsSubmitted ? (
                                    <p className="text-sm font-medium text-cyan-300">{tt("✓ Your details have been submitted — please complete your bank transfer.")}</p>
                                  ) : (
                                    <button type="button" onClick={handleBacsSubmit} className="rounded-xl bg-cyan-400 px-5 py-2.5 text-sm font-semibold text-slate-950 transition hover:bg-cyan-300">
                                      {tt("Submit my details for BACS payment")}
                                    </button>
                                  )}
                                </div>
                              </div>
                            )}
                          </>
                        )}

                        {/* Junior form */}
                        {membershipTab === "junior" && (
                          <>
                            <p className="mt-5 text-xs font-semibold uppercase tracking-[0.2em] text-amber-300/80">{tt("Junior Member Details")}</p>
                            <p className="mt-2 text-sm text-slate-400">{tt("Junior membership is open to members aged")} <span className="font-semibold text-amber-300">{tt("under 16 on 1st January")} {currentYear}</span>.</p>
                            <div className="mt-3 grid gap-4 sm:grid-cols-2">
                              <input
                                type="text"
                                placeholder={tt("Junior's Full Name")}
                                value={juniorName}
                                onChange={(e) => setJuniorName(e.target.value)}
                                className="rounded-2xl border border-white/10 bg-slate-900/80 px-4 py-3 text-white placeholder:text-slate-500 outline-none focus:border-amber-300/40"
                              />
                              <input
                                type="tel"
                                placeholder={tt("Junior's Mobile (if applicable)")}
                                value={juniorMobile}
                                onChange={(e) => setJuniorMobile(e.target.value)}
                                className="rounded-2xl border border-white/10 bg-slate-900/80 px-4 py-3 text-white placeholder:text-slate-500 outline-none focus:border-amber-300/40"
                              />
                              <div className="sm:col-span-2">
                                <label className="mb-1.5 block text-xs text-slate-400">{tt("Junior's Date of Birth")}</label>
                                <input
                                  type="date"
                                  value={juniorDob}
                                  max={new Date().toISOString().split("T")[0]}
                                  onChange={(e) => setJuniorDob(e.target.value)}
                                  className="w-full rounded-2xl border border-white/10 bg-slate-900/80 px-4 py-3 text-white outline-none focus:border-amber-300/40 [color-scheme:dark]"
                                />
                                {juniorDob && (
                                  <p className={`mt-2 text-sm font-medium ${juniorEligible ? "text-green-400" : "text-red-400"}`}>
                                    {`Age ${juniorAgeOnJan1} ${tt("on 1st January")} ${currentYear} — `}
                                    {juniorEligible ? tt("eligible for junior membership.") : tt("not eligible for junior membership.")}
                                  </p>
                                )}
                              </div>
                              <textarea
                                rows={3}
                                placeholder={tt("Full Address including Postcode")}
                                value={juniorAddress}
                                onChange={(e) => setJuniorAddress(e.target.value)}
                                className="sm:col-span-2 rounded-2xl border border-white/10 bg-slate-900/80 px-4 py-3 text-white placeholder:text-slate-500 outline-none focus:border-amber-300/40"
                              />
                            </div>
                            <p className="mt-5 text-xs font-semibold uppercase tracking-[0.2em] text-amber-300/80">{tt("Parent / Guardian Details")}</p>
                            <div className="mt-3 grid gap-4 sm:grid-cols-2">
                              <input
                                type="text"
                                placeholder={tt("Parent / Guardian Full Name")}
                                value={parentName}
                                onChange={(e) => setParentName(e.target.value)}
                                className="rounded-2xl border border-white/10 bg-slate-900/80 px-4 py-3 text-white placeholder:text-slate-500 outline-none focus:border-amber-300/40"
                              />
                              <input
                                type="tel"
                                placeholder={tt("Parent / Guardian Mobile")}
                                value={parentMobile}
                                onChange={(e) => setParentMobile(e.target.value)}
                                className="rounded-2xl border border-white/10 bg-slate-900/80 px-4 py-3 text-white placeholder:text-slate-500 outline-none focus:border-amber-300/40"
                              />
                              <input
                                type="email"
                                placeholder={tt("Parent / Guardian Email Address")}
                                value={parentEmail}
                                onChange={(e) => setParentEmail(e.target.value)}
                                className="sm:col-span-2 rounded-2xl border border-white/10 bg-slate-900/80 px-4 py-3 text-white placeholder:text-slate-500 outline-none focus:border-amber-300/40"
                              />
                            </div>
                            <label className="mt-5 flex items-start gap-3 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={juniorRulesAgreed}
                                onChange={(e) => setJuniorRulesAgreed(e.target.checked)}
                                className="mt-1 h-4 w-4 shrink-0 accent-amber-400"
                              />
                              <span className="text-sm leading-6 text-slate-300">
                                {tt("I (parent/guardian) have read and agree to the")}{" "}
                                <a href="/assets/rsaa_club_rules.pdf" target="_blank" rel="noopener noreferrer" className="text-amber-300 underline hover:text-amber-200">{tt("Club Rules")}</a>
                                {" "}{tt("and the")}{" "}
                                <a href="/assets/rsaa_constitution.pdf" target="_blank" rel="noopener noreferrer" className="text-amber-300 underline hover:text-amber-200">{tt("Club Constitution")}</a>
                                {" "}{tt("on behalf of the junior member.")}
                              </span>
                            </label>
                            {!juniorMembershipReady && (
                              <p className="mt-3 rounded-xl border border-amber-300/20 bg-amber-400/10 px-4 py-2.5 text-sm text-amber-300">{tt("Complete all fields and agree to the Club Rules to unlock payment.")}</p>
                            )}
                            <div className="mt-5 flex flex-col gap-3 sm:flex-row">
                              {/* <a
                                href={juniorMembershipReady ? "https://www.paypal.com/cgi-bin/webscr?cmd=_xclick&business=redruthseaanglingassociation@gmail.com&amount=1.00&currency_code=GBP&item_name=RSAA%20Junior%20Annual%20Membership" : undefined}
                                onClick={handleJuniorMembershipClick}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`flex w-full sm:w-auto items-center justify-center gap-3 rounded-2xl px-8 py-4 text-base font-semibold shadow-xl transition ${
                                  juniorMembershipReady
                                    ? "bg-gradient-to-r from-amber-400 to-yellow-400 text-slate-950 shadow-amber-500/30 hover:scale-[1.05] hover:shadow-amber-400/40 active:scale-[0.97]"
                                    : "cursor-not-allowed bg-slate-700 text-slate-400 shadow-none"
                                }`}
                              >
                                <CreditCard className="h-5 w-5" />
                                Pay £1 via PayPal
                              </a> */}
                              <a
                                href={juniorMembershipReady ? `https://pay.sumup.com/b2c/QTGBECUF?customer-name=${encodeURIComponent(juniorName)}` : undefined}
                                onClick={handleJuniorMembershipClick}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`flex w-full sm:w-auto items-center justify-center gap-3 rounded-2xl px-8 py-4 text-base font-semibold shadow-xl transition ${
                                  juniorMembershipReady
                                    ? "bg-gradient-to-r from-slate-100 to-white text-slate-950 shadow-slate-400/20 hover:scale-[1.05] hover:shadow-slate-300/30 active:scale-[0.97]"
                                    : "cursor-not-allowed bg-slate-700 text-slate-400 shadow-none"
                                }`}
                              >
                                <CreditCard className="h-5 w-5" />
                                {tt("Pay £1 via Card / Bank")}
                              </a>
                            </div>
                            {juniorMembershipReady && (
                              <div className="mt-4 rounded-2xl border border-white/10 bg-white/5 px-5 py-4 panel-lift">
                                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-amber-300">{tt("Pay by Bank Transfer (BACS)")}</p>
                                <p className="mt-1 text-xs text-slate-400">{tt("Please use the junior member's name as the payment reference.")}</p>
                                <div className="mt-3 grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
                                  <span className="text-slate-400">{tt("Account name")}</span>
                                  <span className="font-medium text-white">Redruth Sea Angling Association</span>
                                  <span className="text-slate-400">{tt("Sort code")}</span>
                                  <span className="font-medium text-white">30-97-00</span>
                                  <span className="text-slate-400">{tt("Account number")}</span>
                                  <span className="font-medium text-white">00184090</span>
                                </div>
                                <div className="mt-4 border-t border-white/10 pt-4">
                                  {bacsJuniorSubmitted ? (
                                    <p className="text-sm font-medium text-amber-300">{tt("✓ Your details have been submitted — please complete your bank transfer.")}</p>
                                  ) : (
                                    <button type="button" onClick={handleJuniorBacsSubmit} className="rounded-xl bg-amber-400 px-5 py-2.5 text-sm font-semibold text-slate-950 transition hover:bg-amber-300">
                                      {tt("Submit my details for BACS payment")}
                                    </button>
                                  )}
                                </div>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </motion.section>



<LatestCatches />

{/* Facebook news section */}
                <motion.section initial={{ opacity: 0, y: 32 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-100px" }} transition={{ duration: 0.6, ease: "easeOut" }} id="facebook" className="mx-auto max-w-full px-4 py-8 sm:px-6 lg:px-8">
                  <div className="mx-auto max-w-7xl mb-4">
                    <a href="https://www.facebook.com/profile.php?id=100086902542471" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-xl border border-cyan-300/40 bg-cyan-400/15 px-4 py-2 text-sm font-semibold text-cyan-300 transition hover:bg-cyan-400/25">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5"><path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878V14.89h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z"/></svg>
                      {tt("Visit our Facebook Group")}
                    </a>
                  </div>
                  <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-4">
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-cyan-400/10 text-cyan-300">
                        <Newspaper className="h-5 w-5" />
                      </div>
                      <h2 className="text-3xl font-bold tracking-tight text-white">{tt("Latest RSAA News from FaceBook")}</h2>
                    </div>
                    <button type="button" onClick={() => setNewsOpen((o) => !o)} className="flex items-center gap-2 rounded-xl border border-cyan-300/40 bg-cyan-400/15 px-4 py-2 text-sm font-semibold text-cyan-300 transition hover:bg-cyan-400/25 shrink-0">
                      {newsOpen ? tt("Hide") : tt("Show")}
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className={`h-6 w-6 transition-transform duration-300 ${newsOpen ? "rotate-180" : ""}`}><path fillRule="evenodd" d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06z" clipRule="evenodd" /></svg>
                    </button>
                  </div>
                  <AnimatePresence initial={false}>
                  {newsOpen && (
                  <motion.div
                    key="news-panel"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.35, ease: "easeInOut" }}
                    className="overflow-hidden"
                  >
                  <div className="mt-8 mx-auto max-w-7xl">
                    <div className="overflow-hidden rounded-[2rem]">
                      <FacebookNews />
                    </div>
                  </div>
                  </motion.div>
                  )}
                  </AnimatePresence>

                  <div id="gallery" className="mt-16 mx-auto max-w-7xl">
                    <div className="flex flex-wrap items-center gap-4">
                      <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-cyan-400/10 text-cyan-300">
                          <Images className="h-5 w-5" />
                        </div>
                        <div>
                          <h2 className="text-3xl font-bold tracking-tight text-white">{tt("Photo Gallery")}</h2>
                          <p className="mt-2 text-sm text-slate-400">{tt("Photos from the Redruth Sea Angling Association Facebook page")}</p>
                        </div>
                      </div>
                      <button type="button" onClick={() => setGalleryOpen((o) => !o)} className="flex items-center gap-2 rounded-xl border border-cyan-300/40 bg-cyan-400/15 px-4 py-2 text-sm font-semibold text-cyan-300 transition hover:bg-cyan-400/25 shrink-0">
                        {galleryOpen ? tt("Hide") : tt("Show")}
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className={`h-6 w-6 transition-transform duration-300 ${galleryOpen ? "rotate-180" : ""}`}><path fillRule="evenodd" d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06z" clipRule="evenodd" /></svg>
                      </button>
                    </div>
                    <AnimatePresence initial={false}>
                    {galleryOpen && (
                      <motion.div
                        key="gallery-panel"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.35, ease: "easeInOut" }}
                        className="overflow-hidden"
                      >
                      <div className="mt-8">
                        <FacebookGallery />
                      </div>
                      </motion.div>
                    )}
                    </AnimatePresence>
                  </div>
                </motion.section>

                {/* <canvas id="pdf-canvas" style="border: 1px solid #ccc; width: 100%; max-width: 600px;"></canvas> */}


                <motion.section initial={{ opacity: 0, y: 32 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-100px" }} transition={{ duration: 0.6, ease: "easeOut" }} id="documents" className="px-4 py-8 sm:px-6 lg:px-8">
                  <div className="mx-auto flex max-w-7xl flex-wrap items-start gap-4">
                    <div className="flex items-start gap-3">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-cyan-400/10 text-cyan-300">
                        <FileText className="h-5 w-5" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold uppercase tracking-[0.3em] text-cyan-300">{tt("Rules and documents")}</p>
                        <div className="mt-4 flex flex-wrap items-center gap-4">
                          <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">{tt("RSAA Club Documents")}</h2>
                          <button type="button" onClick={() => setDocumentsOpen((o) => !o)} className="flex items-center gap-2 rounded-xl border border-cyan-300/40 bg-cyan-400/15 px-4 py-2 text-sm font-semibold text-cyan-300 transition hover:bg-cyan-400/25 shrink-0">
                            {documentsOpen ? tt("Hide") : tt("Show")}
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className={`h-6 w-6 transition-transform duration-300 ${documentsOpen ? "rotate-180" : ""}`}><path fillRule="evenodd" d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06z" clipRule="evenodd" /></svg>
                          </button>
                        </div>
                        <p className="mt-4 max-w-xl text-sm leading-7 text-slate-400">{tt("All club rules, policies, and competition documents are available to download below. Click any document to open it as a PDF.")}</p>
                      </div>
                    </div>
                  </div>

                  <AnimatePresence initial={false}>
                  {documentsOpen && (
                    <motion.div
                      key="documents-panel"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.35, ease: "easeInOut" }}
                      className="overflow-hidden"
                    >
                    <div className="mx-auto mt-8 grid max-w-7xl gap-4 sm:grid-cols-2 lg:grid-cols-3">
                      {documentLinks.map((doc) => (
                        <a
                          key={doc.file}
                          href={doc.file}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="rounded-[1.6rem] border border-white/10 bg-white/5 p-5 text-left transition hover:border-cyan-300/30 hover:bg-cyan-400/5 block panel-lift"
                        >
                          <FileText className="h-6 w-6 text-cyan-300" />
                          <p className="mt-4 text-lg font-semibold text-white">{tt(doc.name)}</p>
                        </a>
                      ))}
                    </div>
                    </motion.div>
                  )}
                  </AnimatePresence>
                </motion.section>


                <motion.section initial={{ opacity: 0, y: 32 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-100px" }} transition={{ duration: 0.6, ease: "easeOut" }} id="officers" className="relative overflow-hidden px-4 py-8 sm:px-6 lg:px-8">
                  <img src="/assets/beach.webp" alt="" aria-hidden="true" className="section-bg pointer-events-none absolute inset-0 h-full w-full object-cover" style={{ objectPosition: 'center 50%' }} />
                  <div className="absolute inset-0 bg-gradient-to-b from-slate-950/85 via-slate-950/65 to-slate-950/85" />
                  <div className="relative mx-auto max-w-7xl">
                  <div className="flex items-start gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-cyan-400/10 text-cyan-300">
                      <Users className="h-5 w-5" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold uppercase tracking-[0.3em] text-cyan-300">{tt("Club officers")}</p>
                      <div className="mt-4 flex flex-wrap items-center gap-4">
                        <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">{tt("RSAA Club Officers")}</h2>
                        <button type="button" onClick={() => setOfficersOpen((o) => !o)} className="flex items-center gap-2 rounded-xl border border-cyan-300/40 bg-cyan-400/15 px-4 py-2 text-sm font-semibold text-cyan-300 transition hover:bg-cyan-400/25 shrink-0">
                          {officersOpen ? tt("Hide") : tt("Show")}
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className={`h-6 w-6 transition-transform duration-300 ${officersOpen ? "rotate-180" : ""}`}><path fillRule="evenodd" d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06z" clipRule="evenodd" /></svg>
                        </button>
                      </div>
                    </div>
                  </div>

                    <AnimatePresence initial={false}>
                    {officersOpen && (
                      <motion.div
                        key="officers-panel"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.35, ease: "easeInOut" }}
                        className="overflow-hidden"
                      >
                      <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                        {officers.map((officer) => {
                          const [role, name] = officer.split(": ");
                          return (
                            <div key={officer} className="rounded-[1.6rem] border border-white/10 bg-white/5 p-5 panel-lift">
                              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-cyan-300">{tt(role)}</p>
                              <p className="mt-1 text-sm font-medium text-white">{name}</p>
                            </div>
                          );
                        })}
                      </div>
                      </motion.div>
                    )}
                    </AnimatePresence>
                  </div>
                </motion.section>
              </main>

              <footer className="border-t border-white/10 bg-slate-950">
                <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">

                  {/* Footer sitemap */}
                  <div className="mb-8 border-b border-white/10 pb-8 grid grid-cols-1 divide-y divide-white/10 lg:grid-cols-4 lg:gap-8 lg:divide-y-0">

                    {/* Quick Links */}
                    {footerSection("Quick Links", (
                      <ul className="space-y-2 pb-4 lg:pb-0">
                        {[
                          { label: "Home", to: "/" },
                          { label: "About", to: "/about" },
                          { label: "Fixtures", to: "/fixtures" },
                          { label: "Officers", to: "/officers" },
                          { label: "Sea Conditions", to: "/fishing-conditions" },
                          { label: "News", to: "/news" },
                          { label: "Gallery", to: "/gallery" },
                          { label: "YouTube Channels", to: "/youtube" },
                          { label: "Local Services", to: "/services" },
                          { label: "Tackle & Bait Shops", to: "/tackle-shops" },
                          { label: "Club Payments", to: "/payments" },
                          { label: "Contact", to: "/contact" },
                        ].map(({ label, to }) => (
                          <li key={label}>
                            <Link to={to} className="text-sm text-slate-400 transition hover:text-cyan-300">
                              {tt(label)}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    ), "py-4 lg:py-0")}

                    {/* Records / Sponsors / Angler's Tools */}
                    <div className="py-4 lg:py-0">
                      {footerSection("Records", (
                        <ul className="space-y-2 pb-4 lg:pb-0">
                          <li><Link to="/records"       className="text-sm text-slate-400 transition hover:text-cyan-300">{tt("C&R Records")}</Link></li>
                          <li><Link to="/rsaa-records"  className="text-sm text-slate-400 transition hover:text-cyan-300">{tt("Official Club Records")}</Link></li>
                          <li><Link to="/junior-records" className="text-sm text-slate-400 transition hover:text-cyan-300">{tt("Junior Records")}</Link></li>
                          <li><Link to="/junior-catch-release" className="text-sm text-slate-400 transition hover:text-cyan-300">{tt("Junior C&R Records")}</Link></li>
                          <li><Link to="/awards"        className="text-sm text-slate-400 transition hover:text-cyan-300">{tt("Awards & Champions")}</Link></li>
                        </ul>
                      ))}

                      {footerSection("Sponsors & Merchandise", (
                        <ul className="space-y-2 pb-4 lg:pb-0">
                          <li><Link to="/sponsors"    className="text-sm text-slate-400 transition hover:text-cyan-300">{tt("Club Sponsors")}</Link></li>
                          <li><Link to="/merchandise" className="text-sm text-slate-400 transition hover:text-cyan-300">{tt("Club Merchandise")}</Link></li>
                        </ul>
                      ), "border-t border-white/10 pt-4 lg:border-0 lg:pt-6")}

                      {footerSection("Angler's Tools", (
                        <ul className="space-y-2 pb-4 lg:pb-0">
                          <li><Link to="/cfsa-specimen"          className="text-sm text-slate-400 transition hover:text-cyan-300">{tt("CFSA Specimen Calculator")}</Link></li>
                          <li><Link to="/competition-calculator" className="text-sm text-slate-400 transition hover:text-cyan-300">{tt("Competition Leaderboard")}</Link></li>
                          <li><Link to="/species-hunt"           className="text-sm text-slate-400 transition hover:text-cyan-300">{tt("Species Hunt")}</Link></li>
                          <li><Link to="/ray-bass-hunt"          className="text-sm text-slate-400 transition hover:text-cyan-300">{tt("Ray & Bass Hunt")}</Link></li>
                          <li><Link to="/install"                className="text-sm text-slate-400 transition hover:text-cyan-300">📱 {tt("Add RSAA to Your Home Screen")}</Link></li>
                          <li><Link to="/angler-safety"         className="text-sm text-slate-400 transition hover:text-cyan-300">{tt("Angler Safety & what3words")}</Link></li>
                          <li><Link to="/just-tides"            className="text-sm text-slate-400 transition hover:text-cyan-300">{tt("Just Tides")}</Link></li>
                          <li><Link to="/sea-views"             className="text-sm text-slate-400 transition hover:text-cyan-300">{tt("Sea Views — Live Webcams")}</Link></li>
                          <li><a href="https://xcweather.co.uk/" target="_blank" rel="noopener noreferrer" className="text-sm text-slate-400 transition hover:text-cyan-300">{tt("Detailed Weather Forecast ↗")}</a></li>
                        </ul>
                      ), "border-t border-white/10 pt-4 lg:border-0 lg:pt-6")}
                    </div>

                    {/* Club Documents */}
                    {footerSection("Club Rules & Documents", (
                      <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2 pb-4 lg:pb-0">
                        <li><Link to="/upcoming-meeting" className="text-sm text-slate-400 transition hover:text-cyan-300">{tt("Meeting Agenda & Minutes")}</Link></li>
                        <li><Link to="/scales-policy"    className="text-sm text-slate-400 transition hover:text-cyan-300">{tt("Weighing Scales Policy")}</Link></li>
                        <li><Link to="/weigh-measure"    className="text-sm text-slate-400 transition hover:text-cyan-300">{tt("Weighing & Measuring")}</Link></li>
                        <li><Link to="/sea-angling-strategy" className="text-sm text-slate-400 transition hover:text-cyan-300">{tt("Cornwall Sea Angling Strategy")}</Link></li>
                        <li><a href="/assets/Safeguarding_Policy.pdf"             target="_blank" rel="noopener noreferrer" className="text-sm text-slate-400 transition hover:text-cyan-300">{tt("Safeguarding Policy")}</a></li>
                        <li><a href="/assets/rsaa_club_rules.pdf"                 target="_blank" rel="noopener noreferrer" className="text-sm text-slate-400 transition hover:text-cyan-300">{tt("Club Rules")}</a></li>
                        <li><a href="/assets/rsaa_constitution.pdf"               target="_blank" rel="noopener noreferrer" className="text-sm text-slate-400 transition hover:text-cyan-300">{tt("RSAA Constitution")}</a></li>
                        <li><a href="/assets/club_fixtures_2026.pdf"              target="_blank" rel="noopener noreferrer" className="text-sm text-slate-400 transition hover:text-cyan-300">{tt("Club Fixtures")}</a></li>
                        <li><a href="/assets/Boat_Risk_Assessments.pdf"           target="_blank" rel="noopener noreferrer" className="text-sm text-slate-400 transition hover:text-cyan-300">{tt("Boat Risk Assessments")}</a></li>
                        <li><a href="/assets/rsaa_boat_safety_report.pdf"         target="_blank" rel="noopener noreferrer" className="text-sm text-slate-400 transition hover:text-cyan-300">{tt("Boat Safety Report")}</a></li>
                        <li><a href="/assets/Shore_Fishing_Risk_Assessment.pdf"   target="_blank" rel="noopener noreferrer" className="text-sm text-slate-400 transition hover:text-cyan-300">{tt("Shore Risk Assessment")}</a></li>
                        <li><a href="/assets/month_long_boat_comps.pdf"           target="_blank" rel="noopener noreferrer" className="text-sm text-slate-400 transition hover:text-cyan-300">{tt("Month Long Boat Comps")}</a></li>
                        <li><a href="/assets/month_long_shore_comps.pdf"          target="_blank" rel="noopener noreferrer" className="text-sm text-slate-400 transition hover:text-cyan-300">{tt("Month Long Shore Comps")}</a></li>
                        <li><a href="/assets/rsaa_species_hunt_rules.pdf"         target="_blank" rel="noopener noreferrer" className="text-sm text-slate-400 transition hover:text-cyan-300">{tt("Species Hunt Rules")}</a></li>
                      </ul>
                    ), "py-4 lg:py-0 lg:col-span-2")}

                  </div>
                  <div className="mb-6 rounded-2xl border border-white/10 bg-white/5 px-5 py-5 text-sm leading-7 text-slate-400 space-y-4 border-l-2 border-l-cyan-400/40 panel-lift">
                    <p className="text-sm font-semibold text-slate-300">{tt("Website Disclaimer")}</p>
                    <p>{tt("The information provided on this website is for general guidance only and is published in good faith. While we make every effort to keep content accurate and up to date, we make no warranties regarding its completeness, reliability, or accuracy.")}</p>
                    <p>{tt("Any action you take based on the information found on this website is strictly at your own risk. The club accepts no responsibility for any loss, damage, injury, or inconvenience arising from reliance on this information or from participation in club activities.")}</p>
                    <p>{tt("This website may include links to external websites. While we aim to provide links to useful and reputable sources, we have no control over the content or practices of these sites and accept no responsibility for them.")}</p>
                    <p>{tt("Members and visitors are responsible for their own safety, equipment, and compliance with local fishing regulations, tides, and weather conditions at all times.")}</p>
                    <p className="font-semibold text-slate-300">{tt("Competitions and Events")}</p>
                    <p>{tt("Participation in club competitions and events is entirely at the member's own risk. It is the responsibility of each participant to ensure they are fishing safely, within the law, and in accordance with club rules. The club accepts no liability for personal injury, loss or damage to equipment, or any incidents that may occur before, during, or after club events.")}</p>
                  </div>
                  <div className="mb-8 flex justify-center">
                    <img
                      src="/assets/rsaa_logo_2.webp"
                      alt="Redruth Sea Angling Association"
                      className="h-48 w-auto sm:h-64 opacity-95 drop-shadow-[0_0_30px_rgba(6,182,212,0.25)]"
                    />
                  </div>
                  <div className="flex flex-col gap-3 text-sm text-slate-400 lg:flex-row lg:items-center lg:justify-between">
                    <p><Link to="/admin" className="text-slate-400 inline-block px-2 py-2 -mx-2 -my-2">©</Link> 2026 Redruth Sea Angling Association. {tt("A great Cornish Club for Great Cornish Anglers.")}</p>
                    <div className="flex flex-wrap gap-5">
                      <a href="mailto:tim.clotworthy@btinternet.com" className="flex items-center gap-1.5 transition hover:text-cyan-300">
                        <Mail className="h-4 w-4" /> tim.clotworthy@btinternet.com
                      </a>
                      <a href="tel:07766826693" className="flex items-center gap-1.5 transition hover:text-cyan-300">
                        <Phone className="h-4 w-4" /> 07766 826693
                      </a>
                    </div>
                  </div>
                </div>
              </footer>
            </div>
          );

  return (
    <Router>
      <ScrollToTop />
      {siteHeader}
      <Routes>
        <Route path="/competition-calculator" element={<CompetitionCalculatorPage />} />
        <Route path="/merchandise" element={<MerchandisePage />} />
        <Route path="/sponsors" element={<SponsorsPage />} />
        <Route path="/scales-policy" element={<ScalesPolicyPage />} />
        <Route path="/weigh-measure" element={<WeighMeasurePage />} />
        <Route path="/sea-angling-strategy" element={<SeaAnglingStrategyPage />} />
        <Route path="/content" element={<ContentPage />} />
        <Route path="/members" element={<MembersPage />} />
        <Route path="/upcoming-meeting" element={<UpcomingMeetingPage />} />
        <Route path="/minutes" element={<MinutesPage />} />
        <Route path="/agenda" element={<AgendaPage />} />
        <Route path="/treasurer" element={<TreasurerPage />} />
        <Route path="/records" element={<RecordsPage />} />
        <Route path="/rsaa-records" element={<RSAARecordsPage />} />
        <Route path="/junior-records" element={<JuniorRecordsPage />} />
        <Route path="/junior-catch-release" element={<JuniorCatchReleasePage />} />
        <Route path="/cfsa-specimen" element={<CFSASpecimenPage />} />
        <Route path="/awards" element={<AwardsPage />} />
        <Route path="/species-hunt" element={<RSAASpeciesHuntPage />} />
        <Route path="/ray-bass-hunt" element={<RayBassHuntPage />} />
        <Route path="/admin" element={<AdminPage />} />
        <Route path="/install" element={<BookmarkPage />} />
        <Route path="/services" element={<ServicesPage />} />
        <Route path="/tackle-shops" element={<TackleShopsPage />} />
        <Route path="/angler-safety" element={<AnglerSafetyPage />} />
        <Route path="/just-tides" element={<JustTidesPage />} />
        <Route path="/sea-views" element={<SeaViewsPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/membership" element={<MembershipPage />} />
        <Route path="/fixtures" element={<FixturesPage />} />
        <Route path="/documents" element={<DocumentsPage />} />
        <Route path="/officers" element={<OfficersPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/payments" element={<PaymentsPage />} />
        <Route path="/news" element={<NewsPage />} />
        <Route path="/gallery" element={<GalleryPage />} />
        <Route path="/youtube" element={<YouTubePage />} />
        <Route path="/fishing-conditions" element={<FishingConditionsPage />} />
        <Route path="/fishing-marks" element={<FishingMarksPage />} />

        <Route path="/" element={<HomeRoute homeElement={homeElement} scrollToSection={scrollToSection} />} />
      </Routes>
    </Router>
  );
}



